/**
 * GEX Candlestick Chart Component
 *
 * A candlestick chart with integrated GEX heatmap overlay.
 * Shows volume-based GEX intensity behind price candles.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';
import { GreeksHeatmapPlugin } from '../indicators/GreeksHeatmapPlugin';
import SessionMarkersPlugin from '../indicators/SessionMarkersPlugin';
import { VWAPIndicator } from '../indicators/VWAPIndicator';
import { VolumeProfilePlugin } from '../indicators/VolumeProfilePlugin';
import { NakedPOCPlugin } from '../indicators/NakedPOCPlugin';
import { HVNZonesPlugin } from '../indicators/HVNZonesPlugin';
import { TPOPlugin } from '../indicators/TPOPlugin';
import { CandleProfilePlugin } from '../indicators/CandleProfilePlugin';
import { ThinCandlesPlugin } from '../indicators/ThinCandlesPlugin';
import { RotationsPlugin } from '../indicators/RotationsPlugin';
import { CVDIndicator } from '../indicators/CVDIndicator';
import { ExposureIndicator } from '../indicators/ExposureIndicator';
import { IvSkewShapeIndicator } from '../indicators/IvSkewShapeIndicator';
import { VolFlowIndicator } from '../indicators/VolFlowIndicator';
import { OrderFlowPlugin } from '../indicators/OrderFlowPlugin';
import { QuoteIVIndicator } from '../indicators/QuoteIVIndicator';
import { UnderlyingQuoteIndicator } from '../indicators/UnderlyingQuoteIndicator';
import { OVERLAY_KEYS, OVERLAY_STYLES, overlayRebaseCoeffs } from '../indicators/underlyingOverlays';
import UnderlyingCompare from './UnderlyingCompare';
import CONFIG from '../config';
import { unifiedSSE } from '../hooks/useUnifiedSSE';
import { authFetch } from '../auth/authFetch';
import { formatTime as _formatTime } from '../utils/formatters';
import { getLatestSessionDate } from '../utils/market';

// Display-timeframe window sizes for bucketing the live forming exposure
// row into the Exposure pane series (mirror of GreeksHeatmapPlugin's
// TF_SECONDS; renko timeframes are deliberately absent).
const EXPOSURE_TF_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1d': 86400,
};

// Realized-vol companion line (IV Index): the trailing window is defined in
// WALL-CLOCK seconds, not bars, so the RV horizon stays ~2h whatever the
// chart timeframe (a bar-count window would mean 100min at 5m but a month
// at 1d against the same short-dated IV comparator). Floors/caps keep the
// bar count sane on extreme timeframes.
const RV_WINDOW_SEC = 7200;
const RV_MIN_BARS = 6;
const RV_MAX_BARS = 120;
const RV_GAP_POOL = 20;        // trailing session-open gap returns (weekend + maintenance halt)
const RV_GAPS_PER_YEAR = 252;  // one session-open gap per trading day
const RV_ACTIVE_CALENDAR_FRACTION = (23 * 5) / (24 * 7); // ES active hours share of the calendar week

function rvWindowBars(tfSec) {
  return Math.min(RV_MAX_BARS, Math.max(RV_MIN_BARS, Math.round(RV_WINDOW_SEC / tfSec)));
}

// Zero-mean RV (sum of squared log returns) plus a pooled gap-variance
// component: closed periods deliver real variance that SPX options price,
// so dropping the gap returns entirely would bias RV low vs the IV line.
// Annualized on the same calendar-year basis as the IV solver's tau
// (365.25d), with bar variance accruing only during active hours.
function computeRvSigma(returns, gaps, tfSec) {
  if (!returns || returns.length === 0) return null;
  let barSq = 0;
  for (const r of returns) barSq += r * r;
  const barVar = barSq / returns.length;
  let gapVar = 0;
  if (gaps && gaps.length > 0) {
    let gapSq = 0;
    for (const g of gaps) gapSq += g * g;
    gapVar = gapSq / gaps.length;
  }
  const barsPerYear = ((365.25 * 24 * 3600) / tfSec) * RV_ACTIVE_CALENDAR_FRACTION;
  const annualVar = barVar * barsPerYear + gapVar * RV_GAPS_PER_YEAR;
  return annualVar > 0 ? Math.sqrt(annualVar) : null;
}

// ES candle mute level while an SPX/SPY quote overlay is on: the hollow
// rebased quote candles sit almost exactly on the ES candles (drift is bps),
// so the solid bodies must fade for the 1px outlines to read.
const OVERLAY_DIM_ALPHA = 0.35;

/**
 * Return `color` at the given alpha. Handles #rrggbb hex and rgb()/rgba()
 * strings; any other format is returned unchanged (undimmed beats broken).
 */
function dimColor(color, alpha) {
  const hex = /^#([0-9a-f]{6})$/i.exec(color?.trim() || '');
  if (hex) {
    const n = parseInt(hex[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(color?.trim() || '');
  if (rgb) {
    const parts = rgb[1].split(',').slice(0, 3).map(s => s.trim());
    if (parts.length === 3) return `rgba(${parts.join(', ')}, ${alpha})`;
  }
  return color;
}

/**
 * Extract date range from candles array.
 * Returns start and end dates in YYYY-MM-DD format.
 */
function getDateRangeFromCandles(candles) {
  if (!candles || candles.length === 0) {
    const today = new Date().toISOString().split('T')[0];
    return { startDate: today, endDate: today };
  }

  // Candles have 'time' property (Unix timestamp in seconds)
  const times = candles.map(c => c.time);
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);

  const startDate = new Date(minTime * 1000).toISOString().split('T')[0];
  const endDate = new Date(maxTime * 1000).toISOString().split('T')[0];

  return { startDate, endDate };
}

/**
 * Convert volume candle data to chart format
 * Uses start_time (nanoseconds) as the time coordinate
 */
function volumeCandleToChartFormat(candle) {
  // Convert nanosecond timestamp to seconds
  let time = candle.start_time;
  if (time > 1e15) {
    time = Math.floor(time / 1e9);
  } else if (time > 1e12) {
    time = Math.floor(time / 1e3);
  }

  return {
    time,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
    // Custom data for tooltip
    volume: candle.volume,
    delta: candle.delta,
    cumulativeDelta: candle.cumulative_delta,
    bidVolume: candle.bid_volume,
    askVolume: candle.ask_volume,
    durationSeconds: candle.duration_seconds,
    pocPrice: candle.poc_price,
    isCurrent: candle.is_current || false,
  };
}

function ESChart({
  candles,
  date,
  endDate,                     // End date for date range (defaults to date if not provided)
  timeframe = '5m',
  displayType = 'candle',      // 'candle' or 'line'; fixed per mount, parent must remount via key to change it (App.jsx)
  // Volume candle mode props
  isVolumeChart = false,       // Whether this is a volume-based chart
  volumeSize = 5000,           // Volume per candle (5000 or 10000)
  onVolumeChartReady,          // Callback when volume chart is ready (for live updates)
  expiryMode = '0dte',     // Expiry mode: '0dte' or 'full'
  underlying = 'spx',      // Underlying: 'spx' | 'spy' | 'es'
  showOiDistribution = false,  // Raw OI distribution per strike
  oiDistToggles = { callOi: true, putOi: true, net: true },
  showForwardOi = false,   // T+1: show next session projected curves
  baselineLoading = false, // Baseline calculation loading state
  onBaselineLoadingChange, // Callback for baseline loading state changes
  onGreeksLoadingChange,      // Callback for GEX plugin loading state changes
  showPrevDayLevels = false,   // Previous day high/low/close
  showPriorWeekLevels = false, // Prior week high/low/close/open (PWH/PWL/PWC/PWO)
  showPriorMonthLevels = false, // Prior month high/low/close/open (PMH/PML/PMC/PMO)
  showAsiaLevels = false,      // Asia session high/low (ASH/ASL)
  showLondonLevels = false,    // London session high/low (LDH/LDL)
  showOpeningRange = false,    // Opening range high/low/mid
  showInitialBalance = false,  // Initial balance high/low/mid
  showOpeningPrints = false,   // Opening prints: GOP + RTHOP
  showPivotPoints = false,     // Floor pivot points: P, R1-R3, S1-S3
  showZeroLevels = false,        // Zero Levels (projected-curve zero-crossing levels)
  zeroLevelGreeks = { gex: true, dex: true, vex: true, cex: true }, // Per-greek zero-crossing visibility
  showGammaLevels = false,     // Gamma Levels (at-spot GVWAP, band, walls)
  gammaLevelToggles = { gvwap: true, band: true, walls: true }, // Gamma Levels sub-toggle visibility
  showExpectedMove = false,    // IV-based expected move bands
  showSessionMarkers = false,  // Vertical lines for Globex/London/RTH open
  showVWAP = false,            // VWAP line with deviation bands
  showVWAPBand1 = false,       // VWAP ±1σ standard deviation bands
  showVWAPBand2 = false,       // VWAP ±2σ standard deviation bands
  vwapSessionMode = 'full',    // 'full' or 'split' (full session + RTH overlay)
  showVolumeProfile = false,   // Volume profile histogram
  showNakedPOC = false,        // Naked VP-POC rays (sub-toggle of Vol Profile)
  showNakedTpoPOC = false,     // Naked TPO-POC rays (sub-toggle of TPO Profile)
  showHVNZones = false,        // HVN zone bands (sub-toggle of Vol Profile)
  vpSessionMode = 'full',      // 'full' or 'split' (overnight + RTH)
  showDeltaProfile = false,    // Delta profile (buy/sell imbalance)
  showTPOProfile = false,      // TPO / Market Profile
  tpoRenderMode = 'histogram', // 'histogram' or 'letters'
  tpoSessionScope = 'full',    // 'full' or 'eth-rth'
  tpoDisplayStyle = 'basic',   // 'basic' (stacked) or 'split' (periods)
  showCandleProfiles = false,  // Planning chart: per-30m-candle VP + TPO mini profiles
  candleProfileSessionType = 'eth', // full session window so every candle shown gets a profile
  showCandleProfileVolume = true,   // per-candle volume profile (bars right)
  showCandleProfileTpo = true,      // per-candle TPO profile (bars left)
  candleProfileWidthFraction = 0.45, // per-side fraction of a candle slot used by profiles
  thinCandles = false,         // Planning chart: render fixed-width thin candle bodies
  candleBodyWidthPx = 3,       // thin candle body width in CSS px (when thinCandles)
  showRotations = false,       // Renko chart: render rotation legs (reach-styled) + bias ribbon
  rotationData = null,         // { legs:[{x0,y0,x1,y1,direction,reach}], reachLines, biasSegments, showBias, showReach }
  clearOnEmpty = false,        // clear the series when candles is empty (renko date nav) instead of leaving stale bars
  barSpacing = null,           // override time-scale bar spacing (separation); null = default 6
  showCVD = false,             // CVD (Cumulative Volume Delta) subplot
  showExposure = false,        // Exposure (net GEX/DEX/VEX/CEX) subplot
  exposureMode = 'dealer',     // 'dealer' (OI x dealer ratio) | 'absolute' (OI only)
  showIV = false,              // IV Index line on main chart (left axis)
  showIVStdDev = false,        // IV standard deviation bands (±2σ)
  showIVRealized = false,      // Realized-vol companion line (delivered vs implied, descriptive)
  quoteOverlays = {},          // {overlayKey: bool} per the underlyingOverlays registry (spx/spy/nq/rty/ym/zn); parents build it with buildQuoteOverlays + useMemo over the scalar toggles
  // Phase 2: Order Flow Patterns
  showOrderflowAbsorption = false,   // Absorption zones (1-point buckets, side-colored bands)
  showOrderflowAggressiveOrders = false,  // Aggressive-order levels (big-print folds on imbalance stacks)
  showOrderflowContextProfile = false,    // Prior-session profile fills + POC/value/core lines
  showOrderflowStructure = false,    // Market structure: tier-1/2 pivots + BOS/CHoCH breaks + bias
  showOrderflowStructureDetail = false, // Also render the tier-1 (short-term) pivots + break lines
  showOrderflowRotationLegs = false, // Renko rotation legs zig-zag (gated by the Rotations group)
  showOrderflowRotationReach = false, // Renko rotation p50/p70/p90 reach levels (per pivot + forward)
  rotationBrickSize = 8.0,     // Served renko size for the rotation overlay (8 | 4); selectable in the panel
  showHedgingDemand = false,   // Hedging Demand: per-strike combined hedge flow profile bars
  showHedgeDemandHeatmap = false, // Hedging Demand heatmap sub-layer (time by strike matrix)
  hedgeDemandRealized = false, // Hedging Demand realized mode (actual trailing dS/dIV instead of per-unit)
  showIvSkew = false,         // IV Skew: per-strike call/put IV dots (follows expiryMode), own draggable axis
  ivSkewBand = true,          // IV Skew sub-layer: risk-neutral 25-75 settlement band + median/mode
  showIvSkewShape = false,      // Smile Shape pane: SK/CU/FIT intraday series (chart.addPane)
  showProjectedCurves = false, // Projected GEX/DEX/VEX curves (right edge)
  projectedCurveGreeks = { gex: true, dex: true, vex: true, cex: true }, // Individual curve visibility
  showZeroLevelsLive = false,    // Live Flips sub-layer (per-minute live-book crossings)
  projCurvesLive = false,      // Live sub-layer: today's curve follows the live book + cursor scrub
  expProfileLiveSmile = false, // Exposure Profile source: per-minute refit smile instead of frozen settlement smile
  exposureLiveSmile = false,   // Exposure pane source: live-smile net sums instead of frozen
  showExposureProfile = false, // Exposure Profile: per-strike exposure at current spot
  expProfileToggles = { gex: true, dex: true, vex: true, cex: true },
  exposureProfileMode = 'dealer', // 'dealer' (OI x dealer ratio) | 'absolute' (OI grip)
  showExpProfileHeatmap = false, // Exposure Profile heatmap sub-layer (time by strike matrix)
  showExpProfileT1 = false,    // T+1 Exposure Profile sub-layer (next-session row, right edge)
  expProfileAnchorFrac = null, // Drag-to-place open-session Exposure Profile axis (fraction of pane width; null = left-edge pin)
  expProfileT1AnchorFrac = null, // Drag-to-place T+1 profile axis (fraction of pane width; null = right-edge pin)
  hedgeDemandAnchorFrac = null, // Drag-to-place Hedging Demand axis (fraction of pane width; null = pin one bar budget right of the exposure axis)
  oiProfileAnchorFrac = null,  // Drag-to-place OI distribution developing-session axis (fraction of pane width; null = left-edge pin)
  ivSkewAnchorFrac = null,    // Drag-to-place IV Skew axis (fraction of pane width; null = pin two bar budgets right of the exposure axis)
  showUnderlyingCompare = false,   // Underlying Compare strip: right-docked ES/SPX/SPY per-strike lanes aligned to the price axis
  underlyingCompareMode = 'oi',    // Compare strip fill: 'oi' | 'exposure' | 'volume'
  underlyingCompareGreek = 'gex',  // Compare strip exposure-mode greek: 'gex' | 'dex' | 'vex' | 'cex'
  underlyingCompareZeroLevels = false, // Compare strip: per-lane zero-crossing ticks
  underlyingCompareGammaLevels = false, // Compare strip: per-lane GVWAP + walls
  showOptionsVolume = false,   // Options Volume: per-strike traded volume of the forming/hovered candle (calls/puts/net)
  optVolToggles = { callVol: true, putVol: true, net: true }, // Options Volume call/put/net sub-toggles
  optVolCumulative = false,    // Options Volume cumulative sub-histogram (day-to-date totals on its own "CV" axis)
  optVolSplit = false,         // Options Volume buy/sell split (paired at-ask/at-bid rows per strike)
  optVolFlowNet = false,       // Options Volume flow net (signed net dealer flow residual bar per side)
  showVolFlowPane = false,     // Options Volume Flow pane (intraday net dealer flow time series sub-pane)
  optVolAnchorFrac = null,     // Drag-to-place Options Volume per-candle axis (fraction of pane width; null = offset default)
  optVolCumAnchorFrac = null,  // Drag-to-place Options Volume cumulative axis (fraction of pane width; null = offset default)
  onProfileAnchorChange = null, // (indicatorId, fracOrNull) called on drag release / reset; pages pass their indicator toggle handler
  onLiveCurvesHealth = null,   // (refit|null) latest per-horizon refit health of the live-curve rows; pages feed the side-panel state line
  tooltipHidden = false,        // Hide tooltip entirely
  tooltipPinned = false,       // Pin tooltip to bottom-left instead of following cursor
  levelData = null,            // Level indicator data from useLevelIndicators
  timezone = 'ET',
  onChartReady,
  candleColors = { upColor: '#22c55e', downColor: '#ef4444' },
  hideGridLines = false,       // Hide chart grid lines (global Candle Colors dialog setting)
  options = {}
}) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const exposurePaneRef = useRef(null);
  const ivSkewShapePaneRef = useRef(null);
  const volFlowPaneRef = useRef(null);
  const volFlowIndicatorRef = useRef(null);
  const volFlowSeriesRef = useRef(null);      // latest series from the plugin's onVolFlow
  const ivSkewShapeIndicatorRef = useRef(null);
  const ivSkewShapeSeriesRef = useRef(null);   // latest series from the plugin's onIvSkewShape
  const rvSegmentsRef = useRef([]);           // realized-vol per-day segments
  const greeksPluginRef = useRef(null);
  const sessionMarkersPluginRef = useRef(null);
  const isSessionMarkersAttachedRef = useRef(false);
  const vwapIndicatorRef = useRef(null);
  const volumeProfilePluginRef = useRef(null);
  const nakedPOCPluginRef = useRef(null);
  const hvnZonesPluginRef = useRef(null);
  const tpoPluginRef = useRef(null);
  const candleProfilePluginRef = useRef(null);
  const thinCandlesPluginRef = useRef(null);
  const rotationsPluginRef = useRef(null);
  const priceLineRef = useRef(null);            // explicit current-price line (thin-candle planning chart)
  const candleColorsRef = useRef(candleColors); // latest colors for the price-line helper (no stale closure)
  const thinCandlesRef = useRef(thinCandles);   // latest thinCandles flag for the price-line helper
  const cvdIndicatorRef = useRef(null);
  const exposureIndicatorRef = useRef(null);
  const exposureCandlesRef = useRef([]);  // Cached /api/greeks/exposure candles for weighting-mode flips
  const liveCurvesHealthRef = useRef(null);   // latest row.refit for the selected mode (null until a post-deploy row arrives)
  const refitHealthDepsRef = useRef({ exposureLiveSmile, expiryMode, onLiveCurvesHealth });
  // Stable plugin callback: forwards refit health to the page (side-panel
  // state line) and drives the Exposure pane's slow-greek fade. Reads its
  // deps through a ref so the once-constructed plugin never holds a stale
  // closure.
  const handleRefitHealth = useRef((refit) => {
    liveCurvesHealthRef.current = refit ?? null;
    const { exposureLiveSmile: liveOn, expiryMode: mode, onLiveCurvesHealth: cb } = refitHealthDepsRef.current;
    cb?.(refit ?? null);
    exposureIndicatorRef.current?.setHybridDim(
      !!(liveOn && (mode || '0dte') === 'full' && refit && refit.tail_held > 0));
  }).current;
  // Keep the health callback's deps fresh. On an expiry-mode change the
  // stored health belongs to the OLD mode: clear it (the state line
  // blanks until the next row or fetch re-seeds it) and re-evaluate the
  // pane fade under the new toggles.
  useEffect(() => {
    const modeChanged = refitHealthDepsRef.current.expiryMode !== expiryMode;
    refitHealthDepsRef.current = { exposureLiveSmile, expiryMode, onLiveCurvesHealth };
    if (modeChanged) {
      liveCurvesHealthRef.current = null;
      onLiveCurvesHealth?.(null);
    }
    const h = liveCurvesHealthRef.current;
    exposureIndicatorRef.current?.setHybridDim(
      !!(exposureLiveSmile && (expiryMode || '0dte') === 'full' && h && h.tail_held > 0));
  }, [exposureLiveSmile, expiryMode, onLiveCurvesHealth]);
  const quoteIVIndicatorRef = useRef(null);
  // One UnderlyingQuoteIndicator instance per enabled overlay, keyed by
  // registry key (single useRef holding a map: fixed hook count).
  const quoteIndicatorRefs = useRef({});
  const orderflowPluginRef = useRef(null);
  const isOrderflowAttachedRef = useRef(false);
  const candlesRef = useRef([]);  // Track candles for live updates
  const candlesPropRef = useRef(candles);  // Track candles prop without triggering re-renders
  const timezoneRef = useRef(timezone);  // Track timezone without triggering re-render
  const hideGridLinesRef = useRef(hideGridLines);  // Track grid visibility without recreating the chart
  const hasInitialFitRef = useRef(false);  // Track if initial fitContent has been done
  const prevCandleCountRef = useRef(0);   // Track candle count for fit-on-range-change
  const showOiDistributionRef = useRef(showOiDistribution);
  const showHedgingDemandRef = useRef(showHedgingDemand);  // Visibility ref - avoids stale closure in main effect
  const isFirstOiRenderRef = useRef(true);  // Skip first OI effect render
  const showProjectedCurvesRef = useRef(showProjectedCurves);
  const projectedCurveGreeksRef = useRef(projectedCurveGreeks);
  const showExposureProfileRef = useRef(showExposureProfile);
  const quoteOverlaysRef = useRef(quoteOverlays);  // tooltip ruler gates rows on enabled overlays
  const expProfileTogglesRef = useRef(expProfileToggles);
  const exposureProfileModeRef = useRef(exposureProfileMode);
  const showExpProfileHeatmapRef = useRef(showExpProfileHeatmap);
  const showExpProfileT1Ref = useRef(showExpProfileT1);
  const showHedgeDemandHeatmapRef = useRef(showHedgeDemandHeatmap);
  const expProfileAnchorFracRef = useRef(expProfileAnchorFrac);
  const expProfileT1AnchorFracRef = useRef(expProfileT1AnchorFrac);
  const hedgeDemandAnchorFracRef = useRef(hedgeDemandAnchorFrac);
  const oiProfileAnchorFracRef = useRef(oiProfileAnchorFrac);
  const showIvSkewRef = useRef(showIvSkew);
  const ivSkewAnchorFracRef = useRef(ivSkewAnchorFrac);
  const ivSkewBandRef = useRef(ivSkewBand);
  const showOptionsVolumeRef = useRef(showOptionsVolume);
  const optVolTogglesRef = useRef(optVolToggles);
  const optVolCumulativeRef = useRef(optVolCumulative);
  const optVolSplitRef = useRef(optVolSplit);
  const optVolFlowNetRef = useRef(optVolFlowNet);
  const optVolAnchorFracRef = useRef(optVolAnchorFrac);
  const optVolCumAnchorFracRef = useRef(optVolCumAnchorFrac);
  const showIvSkewShapeRef = useRef(showIvSkewShape);
  const showVolFlowPaneRef = useRef(showVolFlowPane);
  const hedgeDemandRealizedRef = useRef(hedgeDemandRealized);
  const tfSecRef = useRef(300);              // display-timeframe seconds for the live RV path
  const showIVRef = useRef(showIV);
  const showIVRealizedRef = useRef(showIVRealized);
  // Live RV state machine: self-contained (candlesRef is reset from the
  // stale candles prop on every render, so it cannot carry live RV state).
  // Seeded/reconciled by the RV recompute effect; advanced per completed
  // bar in updateCandle. null until the first completed candle or seed.
  const rvLiveStateRef = useRef(null);
  const onProfileAnchorChangeRef = useRef(onProfileAnchorChange);
  const tooltipHiddenRef = useRef(tooltipHidden);
  const tooltipPinnedRef = useRef(tooltipPinned);
  const underlyingRef = useRef(underlying);
  const sessionRatiosRef = useRef({});  // date -> {spx, spy, nq} (spx/spy converter ratios; nq = overlay anchor, relative divergence only)
  // IMPORTANT: Update refs synchronously during render, not in an effect
  // This ensures the refs are always up-to-date when plugin creation effect runs
  showOiDistributionRef.current = showOiDistribution;
  candleColorsRef.current = candleColors;
  thinCandlesRef.current = thinCandles;
  candlesPropRef.current = candles;
  if (candles && candles.length > 0) {
    candlesRef.current = [...candles];
  }
  // Renko charts pass a "renko ..." timeframe and key candles by de-collided brick
  // "slot" seconds. VWAP is aligned to those slots in renko mode so its time points
  // don't add extra bar indices (which would space the bricks out).
  const isRenko = typeof timeframe === 'string' && timeframe.startsWith('renko');
  showProjectedCurvesRef.current = showProjectedCurves;
  projectedCurveGreeksRef.current = projectedCurveGreeks;
  showExposureProfileRef.current = showExposureProfile;
  quoteOverlaysRef.current = quoteOverlays;
  expProfileTogglesRef.current = expProfileToggles;
  exposureProfileModeRef.current = exposureProfileMode;
  showExpProfileHeatmapRef.current = showExpProfileHeatmap;
  showExpProfileT1Ref.current = showExpProfileT1;
  showHedgeDemandHeatmapRef.current = showHedgeDemandHeatmap;
  expProfileAnchorFracRef.current = expProfileAnchorFrac;
  expProfileT1AnchorFracRef.current = expProfileT1AnchorFrac;
  hedgeDemandAnchorFracRef.current = hedgeDemandAnchorFrac;
  oiProfileAnchorFracRef.current = oiProfileAnchorFrac;
  showIvSkewRef.current = showIvSkew;
  ivSkewAnchorFracRef.current = ivSkewAnchorFrac;
  ivSkewBandRef.current = ivSkewBand;
  showOptionsVolumeRef.current = showOptionsVolume;
  optVolTogglesRef.current = optVolToggles;
  optVolCumulativeRef.current = optVolCumulative;
  optVolSplitRef.current = optVolSplit;
  optVolFlowNetRef.current = optVolFlowNet;
  optVolAnchorFracRef.current = optVolAnchorFrac;
  optVolCumAnchorFracRef.current = optVolCumAnchorFrac;
  showIvSkewShapeRef.current = showIvSkewShape;
  showVolFlowPaneRef.current = showVolFlowPane;
  hedgeDemandRealizedRef.current = hedgeDemandRealized;
  tfSecRef.current = EXPOSURE_TF_SECONDS[timeframe] || 300;
  showIVRef.current = showIV;
  showIVRealizedRef.current = showIVRealized;
  onProfileAnchorChangeRef.current = onProfileAnchorChange;
  tooltipHiddenRef.current = tooltipHidden;
  tooltipPinnedRef.current = tooltipPinned;
  underlyingRef.current = underlying;
  showHedgingDemandRef.current = showHedgingDemand;
  const levelDataRef = useRef(levelData);
  levelDataRef.current = levelData;

  const [chartReady, setChartReady] = useState(false);
  const [greeksLoading, setGreeksLoading] = useState(false);
  const onGreeksLoadingChangeRef = useRef(onGreeksLoadingChange);
  onGreeksLoadingChangeRef.current = onGreeksLoadingChange;
  const [ivData, setIvData] = useState([]);
  const ivDataRef = useRef([]);
  const [underlyingQuoteData, setUnderlyingQuoteData] = useState(
    () => Object.fromEntries(OVERLAY_KEYS.map((k) => [k, []])));
  const underlyingQuoteDataRef = useRef(
    Object.fromEntries(OVERLAY_KEYS.map((k) => [k, []])));
  // Bumped when /session-ratios lands so anchor-dependent effects re-run
  // (sessionRatiosRef alone triggers nothing)
  const [ratiosVersion, setRatiosVersion] = useState(0);

  // Report greeksLoading changes to parent
  useEffect(() => {
    onGreeksLoadingChangeRef.current?.(greeksLoading);
  }, [greeksLoading]);

  // Volume candle state
  const [volumeCandles, setVolumeCandles] = useState([]);
  const [volumeLoading, setVolumeLoading] = useState(false);
  const [volumeStats, setVolumeStats] = useState(null);

  // Keep an explicit current-price line on the thin-candle planning chart.
  // There, the native candle bodies are transparent (ThinCandlesPlugin paints
  // them), so the series' built-in price line + axis label inherit that
  // transparent color and vanish. Draw our own line tracking the latest price,
  // colored by direction, to match the main chart. No-op on the main chart
  // (thinCandles false), which keeps its visible built-in price line.
  const updateCurrentPriceLine = useCallback((candle) => {
    const series = seriesRef.current;
    if (!series || !thinCandlesRef.current) return;
    const last = candle
      || (candlesRef.current.length ? candlesRef.current[candlesRef.current.length - 1] : null);
    if (!last || last.close == null || last.open == null) return;
    const cc = candleColorsRef.current;
    const color = last.close >= last.open ? cc.upColor : cc.downColor;
    if (!priceLineRef.current) {
      priceLineRef.current = series.createPriceLine({
        price: last.close,
        color,
        lineWidth: 1,
        lineStyle: 2,           // dashed, matching the main chart's built-in price line
        axisLabelVisible: true,
      });
    } else {
      priceLineRef.current.applyOptions({ price: last.close, color });
    }
  }, []);

  // Update a single candle (for real-time updates)
  const updateCandle = useCallback((candle, isComplete) => {
    if (!seriesRef.current || !candle) {
      console.log('[ESChart] updateCandle skipped - no series or candle');
      return;
    }

    // Ensure time is a number (Unix timestamp in seconds)
    // Handles: time-based candles (time field), volume candles (start_time in ns)
    let time = candle.time ?? candle.start_time;
    if (typeof time !== 'number') time = parseInt(time, 10);
    // Normalize nanosecond/millisecond timestamps to seconds
    if (time > 1e15) time = Math.floor(time / 1e9);
    else if (time > 1e12) time = Math.floor(time / 1e3);
    const normalizedCandle = { ...candle, time };

    // Validate the candle has required fields
    if (isNaN(normalizedCandle.time) || !normalizedCandle.open || !normalizedCandle.close) {
      console.warn('[ESChart] Invalid candle data:', candle);
      return;
    }

    try {
      // Update the series with new candle data
      const updateData = displayTypeRef.current === 'line'
        ? { time: normalizedCandle.time, value: normalizedCandle.close }
        : normalizedCandle;
      seriesRef.current.update(updateData);
      // Track the live forming price (not yet in candlesRef) on the planning chart
      updateCurrentPriceLine(normalizedCandle);
    } catch (err) {
      // This can happen if the candle time is older than existing data
      console.warn('[ESChart] Could not update candle:', err.message);
    }

    // Accumulate completed candles so CVD toggle-on has full history
    if (isComplete) {
      const list = candlesRef.current;
      const idx = list.findIndex(c => c.time === normalizedCandle.time);
      if (idx >= 0) {
        list[idx] = normalizedCandle;
      } else {
        list.push(normalizedCandle);
      }
    }

    // Update CVD indicator with the new candle data
    if (cvdIndicatorRef.current && cvdIndicatorRef.current.isEnabled()) {
      cvdIndicatorRef.current.updateLastCandle(normalizedCandle, null, isComplete);
    }

    // Keep AOI plugin candles in sync so zones extend to latest candle
    if (orderflowPluginRef.current && isComplete) {
      orderflowPluginRef.current.updateCandles(candlesRef.current);
    }

    // Advance the realized-vol companion one point per COMPLETED bar. The
    // RV recompute effect below only re-runs when the candles prop changes
    // (initial fetch / SSE-connect bulk merge), so without this path the
    // line freezes at page load while the IV line keeps ticking via SSE.
    if (isComplete) {
      try {
        const tfSec = tfSecRef.current;
        let st = rvLiveStateRef.current;
        if (!st || st.tfSec !== tfSec) {
          // Timeframe switch or first candle: reset; the recompute effect
          // reseeds history when the refetched prop lands.
          st = { tfSec, lastTime: null, lastClose: null, returns: [], gaps: [], pendingNewSegment: true };
          rvLiveStateRef.current = st;
        }
        const t = normalizedCandle.time;
        const close = normalizedCandle.close;
        const open = normalizedCandle.open ?? close;
        if (close > 0 && (st.lastTime === null || t > st.lastTime)) {
          if (st.lastTime !== null && t - st.lastTime > tfSec * 3) {
            // Session boundary: bank the gap return, restart the chain
            if (open > 0 && st.lastClose > 0) {
              st.gaps.push(Math.log(open / st.lastClose));
              if (st.gaps.length > RV_GAP_POOL) st.gaps.shift();
            }
            st.returns = [];
            st.pendingNewSegment = true;
          } else if (st.lastClose > 0) {
            st.returns.push(Math.log(close / st.lastClose));
            const wb = rvWindowBars(tfSec);
            if (st.returns.length > wb) st.returns.splice(0, st.returns.length - wb);
          }
          st.lastTime = t;
          st.lastClose = close;
          if (showIVRef.current && showIVRealizedRef.current && st.returns.length >= 5) {
            const sigma = computeRvSigma(st.returns, st.gaps, tfSec);
            if (sigma != null && isFinite(sigma)) {
              const newSegment = st.pendingNewSegment || rvSegmentsRef.current.length === 0;
              const point = { time: t, value: sigma };
              // rvSegmentsRef arrays are shared by reference with the
              // indicator (setRealized), so this mutation keeps toggle
              // off/on and IV re-enable redraws current.
              if (newSegment) rvSegmentsRef.current.push([point]);
              else rvSegmentsRef.current[rvSegmentsRef.current.length - 1].push(point);
              st.pendingNewSegment = false;
              quoteIVIndicatorRef.current?.appendRealized(point, newSegment);
            }
          }
        }
      } catch (e) {
        // RV companion must never break candle flow
        console.warn('[ESChart] RV live update failed:', e.message);
      }
    } else {
      // Provisional forming-bar RV: repaints as the bar develops (same
      // repaint contract as the forming candle itself), computed from the
      // committed window plus the forming close. Never committed: not to
      // the state machine, not to the segment arrays; the completed bar's
      // point replaces it at the same timestamp.
      try {
        const tfSec = tfSecRef.current;
        const st = rvLiveStateRef.current;
        const t = normalizedCandle.time;
        const close = normalizedCandle.close;
        if (st && st.tfSec === tfSec && !st.pendingNewSegment
            && st.lastTime !== null && t > st.lastTime
            && t - st.lastTime <= tfSec * 3  // across a session gap the forming return is a GAP return, not a bar return
            && st.lastClose > 0 && close > 0
            && st.returns.length >= 5
            && rvSegmentsRef.current.length > 0
            && showIVRef.current && showIVRealizedRef.current) {
          const w = st.returns.slice(-(rvWindowBars(tfSec) - 1));
          w.push(Math.log(close / st.lastClose));
          const sigma = computeRvSigma(w, st.gaps, tfSec);
          if (sigma != null && isFinite(sigma)) {
            quoteIVIndicatorRef.current?.updateRealizedForming({ time: t, value: sigma });
          }
        }
      } catch (e) {
        // provisional draw must never break candle flow
      }
    }

    // Check if price fills any AOI levels (zero prints, gaps, imbalances)
    if (orderflowPluginRef.current && orderflowPluginRef.current.isEnabled()) {
      orderflowPluginRef.current.checkForFills(normalizedCandle.high, normalizedCandle.low, normalizedCandle.open, normalizedCandle.close);
    }

  }, [updateCurrentPriceLine]);

  // Keep timezone ref in sync and force time axis redraw
  useEffect(() => {
    timezoneRef.current = timezone;
    // Force chart to redraw time scale when timezone changes
    const formatTime = (timestamp) => _formatTime(timestamp, timezoneRef.current);
    if (chartRef.current) {
      chartRef.current.applyOptions({
        localization: { timeFormatter: formatTime },
        timeScale: { tickMarkFormatter: formatTime },
      });
    }
  }, [timezone]);

  // Initialize chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const containerWidth = chartContainerRef.current.clientWidth;
    const containerHeight = chartContainerRef.current.clientHeight;

    if (containerWidth === 0 || containerHeight === 0) {
      console.warn('[ESChart] Container has no dimensions');
      return;
    }

    // Time formatter - uses ref so timezone can change without recreating chart
    const formatTime = (timestamp) => _formatTime(timestamp, timezoneRef.current);

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: containerWidth,
      height: containerHeight,
      localization: {
        timeFormatter: formatTime,
      },
      layout: {
        background: { color: '#1a1a1a' },
        textColor: '#d1d5db',
      },
      grid: {
        vertLines: { color: '#2a2a2a', visible: !hideGridLinesRef.current },
        horzLines: { color: '#2a2a2a', visible: !hideGridLinesRef.current },
      },
      rightPriceScale: {
        visible: true,
        borderVisible: true,
        borderColor: '#2a2a2a',
        autoScale: true,
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      leftPriceScale: {
        visible: false,  // Will be enabled when CVD is enabled
        borderVisible: true,
        borderColor: '#2a2a2a',
        autoScale: true,
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#2a2a2a',
        rightOffset: 5,
        barSpacing: barSpacing != null ? barSpacing : 6,
        minBarSpacing: 0.1,
        tickMarkFormatter: formatTime,
      },
      crosshair: {
        mode: 0,
        vertLine: {
          width: 1,
          color: '#758696',
          style: 3,
        },
        horzLine: {
          width: 1,
          color: '#758696',
          style: 3,
        },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    // Add series based on display type. When thinCandles is on (planning chart),
    // the native candle body is made transparent and ThinCandlesPlugin paints
    // fixed-width bodies instead - see the thin-candles effect below.
    const TRANSPARENT = 'rgba(0,0,0,0)';
    const bodyUp = thinCandles ? TRANSPARENT : candleColors.upColor;
    const bodyDown = thinCandles ? TRANSPARENT : candleColors.downColor;
    const series = displayType === 'line'
      ? chart.addSeries(LineSeries, {
          color: candleColors.upColor,
          lineWidth: 2,
          priceScaleId: 'right',
        })
      : chart.addSeries(CandlestickSeries, {
          upColor: bodyUp,
          downColor: bodyDown,
          borderUpColor: bodyUp,
          borderDownColor: bodyDown,
          wickUpColor: bodyUp,
          wickDownColor: bodyDown,
          priceScaleId: 'right',
        });

    chartRef.current = chart;
    seriesRef.current = series;
    // Store initial colors so the live-update effect can skip the first run
    seriesRef._lastColors = JSON.stringify(candleColors);

    // Create tooltip
    const tooltip = document.createElement('div');
    tooltip.className = 'gex-chart-tooltip';
    tooltip.style.cssText = `
      position: absolute;
      display: none;
      padding: 12px;
      box-sizing: border-box;
      font-size: 12px;
      color: #d1d5db;
      background: rgba(26, 26, 26, 0.95);
      border: 1px solid #2a2a2a;
      border-radius: 6px;
      pointer-events: none;
      z-index: 1000;
      font-family: 'Courier New', monospace;
      line-height: 1.6;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    `;
    chartContainerRef.current.appendChild(tooltip);

    const fmtExp = (val) => {
      if (val == null || val === 0) return '-';
      const sign = val >= 0 ? '+' : '';
      if (Math.abs(val) >= 1e9) return `${sign}${(val / 1e9).toFixed(1)}B`;
      if (Math.abs(val) >= 1e6) return `${sign}${(val / 1e6).toFixed(0)}M`;
      return `${sign}${val.toFixed(0)}`;
    };

    // ET session date (YYYY-MM-DD) for a unix-seconds timestamp: calendar
    // date in ET, rolled to the next weekday from 18:00 ET (Globex open),
    // same convention as getLatestSessionDate.
    const sessionDateForTime = (timeSec) => {
      const dt = new Date(timeSec * 1000);
      const dateStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(dt);
      const hour = parseInt(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' }).format(dt), 10);
      if (hour >= 18) {
        const d = new Date(dateStr + 'T12:00:00');
        do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
        return d.toISOString().split('T')[0];
      }
      return dateStr;
    };

    // Overlay ruler for the tooltip header: the ES cursor row plus one row
    // per ENABLED Index Overlay showing that index's ACTUAL traded price at
    // the hovered 1m bucket, its cursor-strike conversion (SPX/SPY only:
    // strike identity), its RELATIVE ES position (overlayRebaseCoeffs:
    // where its rebased candle sits on the ES axis, incl. any gain
    // compression), and drift. Anchor
    // resolution order for the strike cells: per-day session-ratios map
    // (always fetched), then the OI-bars session's
    // ratio_spx/ratio_spy (either may be null on a ratio-fetch error), then
    // es_spx_ratio (always present on the OI-bars payload; used for the
    // selected underlying only).
    const buildStrikeLine = (time, price, esClose = null, strikeEs = null) => {
      if (price == null || !isFinite(price)) return '';
      const plugin = greeksPluginRef.current;
      const sessionDate = time != null ? sessionDateForTime(time) : getLatestSessionDate();

      // OI-bars session: latest session opened at or before the hover time;
      // else the earliest FUTURE session (T+1 forward bars drawn right of
      // the candles); else the latest session
      const oiBars = plugin?.getOIBarsData();
      let session = null;
      if (oiBars) {
        const sessions = Object.values(oiBars);
        for (const s of sessions) {
          if (time != null && s.globex_open_ts != null && s.globex_open_ts <= time) {
            if (!session || s.globex_open_ts > session.globex_open_ts) session = s;
          }
        }
        if (!session) {
          const ref = time != null ? time : Math.floor(Date.now() / 1000);
          for (const s of sessions) {
            if (s.globex_open_ts > ref && (!session || s.globex_open_ts < session.globex_open_ts)) session = s;
          }
        }
        if (!session) {
          for (const s of sessions) {
            if (!session || s.rth_open_ts > session.rth_open_ts) session = s;
          }
        }
      }

      // Per-day ANCHOR ratios (T-1 convention: the same ratios that place
      // the levels and draw the rebased quote candles); walk back a few
      // days for holiday hovers. The live ratio is deliberately NOT
      // applied to the value column: those cells are the exact inverse of
      // the drawn geometry (cursor on the SPY candle's high reads exactly
      // SPY's high); intraday truth lives in the live-ES and drift columns.
      let anchorSpx = null;
      let anchorSpy = null;
      const ratioMap = sessionRatiosRef.current;
      let key = sessionDate;
      for (let i = 0; i < 6 && key; i++) {
        const r = ratioMap[key];
        if (r && (r.spx != null || r.spy != null)) {
          anchorSpx = r.spx ?? null;
          anchorSpy = r.spy ?? null;
          break;
        }
        const d = new Date(key + 'T12:00:00');
        do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
        key = d.toISOString().split('T')[0];
      }
      if (anchorSpx == null) anchorSpx = session?.ratio_spx ?? null;
      if (anchorSpy == null) anchorSpy = session?.ratio_spy ?? null;
      const selected = underlyingRef.current || 'spx';
      if (anchorSpx == null && selected !== 'spy') anchorSpx = session?.es_spx_ratio ?? null;
      if (anchorSpy == null && selected === 'spy') anchorSpy = session?.es_spx_ratio ?? null;

      // Drift of the hovered bucket vs the anchor (same-bucket closes:
      // quote bucket close vs ES candle close, never the crosshair y).
      // Blank when unavailable (quote toggles off, overnight, no candle).
      const driftFor = (indicator) => {
        if (typeof time !== 'number' || esClose == null) return '';
        const q = indicator?.getValueAt(time);
        if (!q || q.price == null || q.anchor == null || q.price <= 0) return '';
        const bps = ((esClose / q.price) / q.anchor - 1) * 1e4;
        return `${bps >= 0 ? '+' : ''}${bps.toFixed(1)}bp`;
      };

      // Strike + live-ES cells only when the options strike layer is
      // displayed (the exposure profile / OI histogram) and a drawn strike
      // was matched to the cursor; the value and drift columns are the
      // general ruler and stay unconditional. The ES row's strike cell is
      // the bar's exact drawn axis position; the SPX/SPY cells divide it
      // back to the strike in their own points (an SPX 6900 strike can
      // read 6900.1: the residue of the writer's 0.25 ES tick rounding,
      // max 0.125 ES pts, not a different strike).
      const strikeLayerOn = showExposureProfileRef.current || showOiDistributionRef.current;
      const strikeCellFor = (v, anchor, decimals) => {
        if (strikeEs == null || !strikeLayerOn) return '';
        const sv = strikeEs / anchor;
        const dist = v - sv;
        return `${sv.toFixed(decimals)} (${dist >= 0 ? '+' : ''}${dist.toFixed(decimals)})`;
      };

      // Live ES print for the CURSOR level, shown ONCE (identical on every
      // One row per ENABLED overlay (Index Overlays parent AND child:
      // quoteOverlays is already gated), showing the index's ACTUAL traded
      // price at the hovered 1m bucket (getValueAt: the real quote close,
      // not a cursor conversion) and its RELATIVE ES position
      // (overlayRebaseCoeffs: exactly where its rebased candle sits on
      // the ES axis, incl. any gain compression). SPX/SPY additionally
      // keep their cursor-strike cells (strike identity); overlays with
      // no data at the hovered time (cash overlays overnight, gaps)
      // contribute no row. The ES row always stays first with the cursor
      // price.
      const rows = [['ES', '#d1d5db', price.toFixed(2),
        strikeCellFor(price, 1, 2), '', '']];
      for (const key of OVERLAY_KEYS) {
        if (!quoteOverlaysRef.current[key]) continue;
        const ind = quoteIndicatorRefs.current[key];
        const q = ind?.getValueAt(time);
        if (!q || q.price == null || q.price <= 0) continue;
        const style = OVERLAY_STYLES[key];
        const coeffs = q.anchor != null
          ? overlayRebaseCoeffs(key, q.anchor, q.anchorEs ?? null) : null;
        const relEs = coeffs
          ? `<span style="color:#8b92b2;">ES </span>${(q.price * coeffs.m + coeffs.b).toFixed(2)}`
          : '';
        let strike = '';
        if (key === 'spx' && anchorSpx) strike = strikeCellFor(price / anchorSpx, anchorSpx, 1);
        if (key === 'spy' && anchorSpy) strike = strikeCellFor(price / anchorSpy, anchorSpy, 2);
        rows.push([style.title, style.up,
          q.price.toFixed(style.decimals),
          strike,
          relEs,
          driftFor(ind)]);
      }
      const cells = rows.map(([label, color, value, strike, liveEs, drift]) =>
        `<span style="color:${color};">${label}</span>` +
        `<span style="text-align:right;">${value}</span>` +
        `<span style="text-align:right;">${strike}</span>` +
        `<span style="text-align:right;">${liveEs}</span>` +
        `<span style="text-align:right;">${drift}</span>`
      ).join('');
      return `<div style="display:grid;grid-template-columns:auto auto auto auto auto;gap:1px 10px;justify-content:start;color:#d1d5db;font-size:11px;font-variant-numeric:tabular-nums;">${cells}</div>`;
    };

    // Nearest displayed strike to the crosshair price: the exposure-rows
    // column at or before the hover time when the Exposure Profile is
    // displayed (at ANY hover time: outside RTH no new columns exist, so
    // the values are simply constant: nearest column), else the static
    // pre-RTH OI bars. The matched level's `price` is the strike's exact
    // ES-space position (strike x session ratio, tick-rounded at the
    // writer), which is what the ruler's strike and live-ES cells read.
    const findHoveredLevel = (time, crosshairPrice, isPreRTH) => {
      let level = null;
      let usedExposureRows = false;

      if (showExposureProfileRef.current && crosshairPrice != null) {
        const expData = greeksPluginRef.current?.exposureRowsData;
        const hoverDate = time != null ? sessionDateForTime(time) : getLatestSessionDate();
        let candles = expData?.[hoverDate]?.candles;
        if (!candles?.length && expData) {
          // Session has no rows yet (e.g. evening hover mapping to the next
          // session): constant book from the latest session available
          const dates = Object.keys(expData).filter(d => expData[d]?.candles?.length).sort();
          if (dates.length) candles = expData[dates[dates.length - 1]].candles;
        }
        if (candles?.length) {
          let col = null;
          for (const c of candles) {
            if (time == null || c.time <= time) {
              if (!col || c.time > col.time) col = c;
            }
          }
          if (!col) col = candles[0]; // hover before the first row: constant
          let minDist = Infinity;
          for (const lv of (col.levels || [])) {
            if (lv.price == null) continue;
            const dist = Math.abs(lv.price - crosshairPrice);
            if (dist < minDist) { minDist = dist; level = lv; }
          }
          if (level) usedExposureRows = true;
        }
      }

      if (!usedExposureRows && isPreRTH) {
        // Pre-RTH (or the no-candle zone right of the chart): static OI bars
        const oiBars = greeksPluginRef.current?.getOIBarsData();
        if (oiBars && crosshairPrice != null) {
          let matchedSession = null;
          // Find the session whose premarket covers this candle time
          for (const session of Object.values(oiBars)) {
            const { bars, globex_open_ts, rth_open_ts } = session;
            if (!bars || bars.length === 0) continue;
            if (time != null && time >= globex_open_ts && time < rth_open_ts) {
              matchedSession = session;
              break;
            }
          }
          // Fallback: if no session matched (e.g. after 4 PM), use the latest session
          if (!matchedSession) {
            for (const session of Object.values(oiBars)) {
              if (!matchedSession || session.rth_open_ts > matchedSession.rth_open_ts) {
                matchedSession = session;
              }
            }
          }
          if (matchedSession?.bars) {
            let minDist = Infinity;
            for (const bar of matchedSession.bars) {
              const dist = Math.abs(bar.price - crosshairPrice);
              if (dist < minDist) {
                minDist = dist;
                level = bar;
              }
            }
          }
        }
      }

      return { level, usedExposureRows };
    };

    // Tooltip handler
    chart.subscribeCrosshairMove((param) => {
      if (tooltipHiddenRef.current) {
        tooltip.style.display = 'none';
        return;
      }
      if (!param.point || param.point.x < 0 || param.point.y < 0) {
        tooltip.style.display = 'none';
        return;
      }

      const data = param.seriesData.get(series);

      if (!param.time && !data) {
        // No candle under crosshair (left margin / future zone right of the
        // candles): strike line with the nearest displayed strike, plus
        // curve values when shown
        const crosshairPrice = series.coordinateToPrice(param.point.y);
        const { level: zoneLevel } = findHoveredLevel(null, crosshairPrice, true);
        const strikeLine = buildStrikeLine(null, crosshairPrice, null, zoneLevel?.price ?? null);
        const curveVals = showProjectedCurvesRef.current
          ? greeksPluginRef.current?.getCurveValuesAtPrice(crosshairPrice)
          : null;
        const hasCurve = curveVals && (curveVals.gex != null || curveVals.dex != null || curveVals.vex != null || curveVals.cex != null);
        if (!strikeLine && !hasCurve) {
          tooltip.style.display = 'none';
          return;
        }
        let curveGrid = '';
        if (hasCurve) {
          const cGexColor = curveVals.gex >= 0 ? '#3b82f6' : '#f97316';
          const cDexColor = curveVals.dex >= 0 ? '#ef4444' : '#22c55e';
          const cVexColor = curveVals.vex >= 0 ? '#a855f7' : '#fbbf24';
          const cCexColor = curveVals.cex >= 0 ? '#06b6d4' : '#e11d48';
          curveGrid = `<div style="display: grid; grid-template-columns: auto 1fr; gap: 8px;">
            <div style="color: #8b92b2;">P.GEX:</div><div style="color: ${cGexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.gex)}</div>
            <div style="color: #8b92b2;">P.DEX:</div><div style="color: ${cDexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.dex)}</div>
            <div style="color: #8b92b2;">P.VEX:</div><div style="color: ${cVexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.vex)}</div>
            <div style="color: #8b92b2;">P.CEX:</div><div style="color: ${cCexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.cex)}</div>
          </div>`;
        }
        tooltip.innerHTML = `
          <div style="${curveGrid ? 'margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #2a2a2a;' : ''}">
            ${strikeLine}
          </div>
          ${curveGrid}
        `;
        if (tooltipPinnedRef.current) {
          tooltip.style.left = '8px';
          tooltip.style.top = '';
          tooltip.style.bottom = '40px';
        } else {
          tooltip.style.bottom = '';
          const tooltipWidth = 260;
          const tooltipHeight = 100;
          let left = param.point.x + 10;
          let top = param.point.y + 10;
          if (left + tooltipWidth > chartContainerRef.current.clientWidth) left = param.point.x - tooltipWidth - 10;
          if (top + tooltipHeight > chartContainerRef.current.clientHeight) top = param.point.y - tooltipHeight - 10;
          tooltip.style.left = left + 'px';
          tooltip.style.top = top + 'px';
        }
        tooltip.style.display = 'block';
        return;
      }

      let formattedTime = '';
      let currentTimezone = timezoneRef.current;
      if (param.time) {
        const dateObj = new Date(param.time * 1000);
        const timeZone = currentTimezone === 'ET' ? 'America/New_York' : 'UTC';
        formattedTime = dateObj.toLocaleString('en-US', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }

      // Determine if pre-RTH: before 9:30 ET or after 4:00 PM ET
      let isPreRTH = false;
      if (param.time) {
        const etTime = new Date(param.time * 1000).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false });
        const [etH, etM] = etTime.split(':').map(Number);
        const etMins = etH * 60 + etM;
        isPreRTH = etMins < 570 || etMins >= 960;
      }

      // Get price at crosshair y-position
      const crosshairPrice = series.coordinateToPrice(param.point.y);

      // Look up per-strike greeks at the crosshair price. Primary source
      // when the Exposure Profile is displayed: the exposure-rows column at
      // or before the hover time, at ANY hover time (outside RTH no new
      // columns exist, so the values are simply constant: nearest column).
      // Fallback (profile off): static pre-RTH OI bars, as before.
      const { level, usedExposureRows } = findHoveredLevel(param.time ?? null, crosshairPrice, isPreRTH);
      let gex = null, dex = null, vex = null, cex = null;
      if (level) {
        if (usedExposureRows) {
          // Match the fields the profile draws (dealer vs absolute u_*)
          const abs = exposureProfileModeRef.current === 'absolute';
          gex = abs ? level.u_gex : level.gex;
          dex = abs ? level.u_dex : level.dex;
          vex = abs ? level.u_vex : level.vex;
          cex = abs ? level.u_cex : level.cex;
        } else {
          gex = level.gex;
          dex = level.dex;
          vex = level.vex;
          cex = level.cex;
        }
      }

      const hasGreeks = gex != null || dex != null || vex != null || cex != null;
      const label = (!usedExposureRows && isPreRTH) ? 'OI ' : '';

      // Ruler: cursor values always; strike + live-ES cells from the
      // matched displayed level (data = ES candle at param.time; .value
      // covers line display mode)
      const strikeLine = buildStrikeLine(param.time, crosshairPrice, data?.close ?? data?.value ?? null, level?.price ?? null);

      // Greeks rows or OHLC fallback
      // Colors derived from intensity fields to match heatmap cell colors.
      // The heatmap uses per-candle normalized intensity (split-max with pow(0.7) rolloff);
      // using the raw delta sign would disagree during reversals.
      const gexIntensity = level?.intensity ?? 0;
      const dexIntensity = level?.dex_intensity ?? 0;
      const vexIntensity = level?.vex_intensity ?? 0;
      const cexIntensity = level?.cex_intensity ?? 0;
      const gexColor = gexIntensity >= 0 ? '#3b82f6' : '#f97316';   // Blue(+) / Orange(-)
      const dexColor = dexIntensity >= 0 ? '#ef4444' : '#22c55e';   // Red(+) / Green(-)
      const vexColor = vexIntensity >= 0 ? '#a855f7' : '#fbbf24';   // Purple(+) / Yellow(-)
      const cexColor = cexIntensity >= 0 ? '#06b6d4' : '#e11d48';   // Cyan(+) / Rose(-)

      // Projected curve values at crosshair price
      let curveRows = '';
      const curveVals = showProjectedCurvesRef.current
        ? greeksPluginRef.current?.getCurveValuesAtPrice(crosshairPrice, param.time)
        : null;
      if (curveVals && (curveVals.gex != null || curveVals.dex != null || curveVals.vex != null || curveVals.cex != null)) {
        const cg = projectedCurveGreeksRef.current;
        const cGexColor = curveVals.gex >= 0 ? '#3b82f6' : '#f97316';
        const cDexColor = curveVals.dex >= 0 ? '#ef4444' : '#22c55e';
        const cVexColor = curveVals.vex >= 0 ? '#a855f7' : '#fbbf24';
        const cCexColor = curveVals.cex >= 0 ? '#06b6d4' : '#e11d48';
        const curveItems = [
          cg.gex && `<div style="color: #8b92b2;">P.GEX:</div><div style="color: ${cGexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.gex)}</div>`,
          cg.dex && `<div style="color: #8b92b2;">P.DEX:</div><div style="color: ${cDexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.dex)}</div>`,
          cg.vex && `<div style="color: #8b92b2;">P.VEX:</div><div style="color: ${cVexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.vex)}</div>`,
          cg.cex && `<div style="color: #8b92b2;">P.CEX:</div><div style="color: ${cCexColor}; text-align: right; font-weight: 600;">${fmtExp(curveVals.cex)}</div>`,
        ].filter(Boolean);
        if (curveItems.length > 0) {
          curveRows = `<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #2a2a2a;">
            <div style="display: grid; grid-template-columns: auto 1fr; gap: 8px;">
              ${curveItems.join('')}
            </div>
          </div>`;
        }
      }

      let bodyRows;
      if (hasGreeks) {
        bodyRows = `<div style="display: grid; grid-template-columns: auto 1fr; gap: 8px;">
            <div style="color: #8b92b2;">${label}GEX:</div><div style="color: ${gexColor}; text-align: right; font-weight: 600;">${fmtExp(gex)}</div>
            <div style="color: #8b92b2;">${label}DEX:</div><div style="color: ${dexColor}; text-align: right; font-weight: 600;">${fmtExp(dex)}</div>
            <div style="color: #8b92b2;">${label}VEX:</div><div style="color: ${vexColor}; text-align: right; font-weight: 600;">${fmtExp(vex)}</div>
            <div style="color: #8b92b2;">${label}CEX:</div><div style="color: ${cexColor}; text-align: right; font-weight: 600;">${fmtExp(cex)}</div>
          </div>${curveRows}`;
      } else if (curveRows) {
        // No Greeks, but have projected curve values
        bodyRows = curveRows.replace(/margin-top: 6px; padding-top: 6px; border-top: 1px solid #2a2a2a;/, '');
      } else {
        // Header-only tooltip: time + ES/SPX/SPY strike line
        bodyRows = '';
      }

      tooltip.innerHTML = `
        <div style="${bodyRows ? 'margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #2a2a2a;' : ''}">
          <div style="color: #8b92b2; font-size: 10px; font-weight: 600;">${currentTimezone}${isPreRTH && hasGreeks ? ' (OI)' : ''}</div>
          <div style="color: #6366f1; font-weight: 600;">${formattedTime}</div>
          ${strikeLine}
        </div>
        ${bodyRows}
      `;

      // Position tooltip
      if (tooltipPinnedRef.current) {
        tooltip.style.left = '8px';
        tooltip.style.top = '';
        tooltip.style.bottom = '40px';
      } else {
        tooltip.style.bottom = '';
        const tooltipWidth = 260;
        const tooltipHeight = 140;
        let left = param.point.x + 10;
        let top = param.point.y + 10;

        if (left + tooltipWidth > chartContainerRef.current.clientWidth) {
          left = param.point.x - tooltipWidth - 10;
        }
        if (top + tooltipHeight > chartContainerRef.current.clientHeight) {
          top = param.point.y - tooltipHeight - 10;
        }

        tooltip.style.left = left + 'px';
        tooltip.style.top = top + 'px';
      }
      tooltip.style.display = 'block';
    });

    setChartReady(true);

    // Notify parent with chart, series, and updateCandle method
    if (onChartReady) {
      onChartReady(chart, series, chartContainerRef.current, updateCandle);
    }

    // Also notify volume chart ready callback for live volume candle updates
    if (onVolumeChartReady) {
      onVolumeChartReady(chart, series, chartContainerRef.current, updateCandle);
    }

    // Handle resize - use ResizeObserver on chart container so the chart
    // updates when sibling elements (e.g. exposure subchart) appear/disappear,
    // not only on window resize.
    const resizeObserver = new ResizeObserver(() => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    });
    resizeObserver.observe(chartContainerRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      seriesRef.current = null;
      setChartReady(false);
    };
  }, [onChartReady, onVolumeChartReady]);

  // Mute the ES candles while a quote overlay is drawn (the hollow rebased
  // candles are invisible against full-strength solid bodies); restore when
  // the overlay is off. Volume charts never draw the overlay (see the wire
  // gate below), so they are never dimmed. Primitive color deps: the
  // candleColors default is a fresh object literal every render.
  const quoteOverlayActive = OVERLAY_KEYS.some((k) => quoteOverlays[k]) && !isVolumeChart;
  const effectiveCandleColors = useMemo(() => {
    // User opacity (Candle Colors dialog) compounds with the overlay dim
    const alpha = (quoteOverlayActive ? OVERLAY_DIM_ALPHA : 1) * (candleColors.opacity ?? 1);
    return alpha < 1
      ? { upColor: dimColor(candleColors.upColor, alpha),
          downColor: dimColor(candleColors.downColor, alpha) }
      : candleColors;
  }, [candleColors.upColor, candleColors.downColor, candleColors.opacity, quoteOverlayActive]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Live-update candlestick colors without recreating the chart.
  // On the planning chart (thinCandles) keep the native body transparent - the
  // visible bodies are painted by ThinCandlesPlugin at a fixed width.
  useEffect(() => {
    if (!seriesRef.current) return;
    const key = JSON.stringify(effectiveCandleColors) + (thinCandles ? ':thin' : '');
    if (seriesRef._lastColors === key) return;
    seriesRef._lastColors = key;
    if (displayTypeRef.current === 'line') {
      seriesRef.current.applyOptions({ color: effectiveCandleColors.upColor });
      return;
    }
    const T = 'rgba(0,0,0,0)';
    const bodyUp = thinCandles ? T : effectiveCandleColors.upColor;
    const bodyDown = thinCandles ? T : effectiveCandleColors.downColor;
    seriesRef.current.applyOptions({
      upColor: bodyUp,
      downColor: bodyDown,
      borderUpColor: bodyUp,
      borderDownColor: bodyDown,
      wickUpColor: bodyUp,
      wickDownColor: bodyDown,
    });
  }, [effectiveCandleColors, thinCandles]);

  // Live-toggle grid lines without recreating the chart. The ref keeps the
  // current value for the create path in case the chart is rebuilt later.
  useEffect(() => {
    hideGridLinesRef.current = hideGridLines;
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      grid: {
        vertLines: { visible: !hideGridLines },
        horzLines: { visible: !hideGridLines },
      },
    });
  }, [hideGridLines]);

  // Helper: convert OHLC candle to line data point
  const toLineData = useCallback((candle) => ({ time: candle.time, value: candle.close }), []);

  // displayType is fixed for the lifetime of a mount (App.jsx remounts via
  // key={displayType}); the ref exists so callbacks avoid a stale closure
  const displayTypeRef = useRef(displayType);

  // Fetch volume candles when in volume chart mode
  useEffect(() => {
    if (!isVolumeChart || !chartReady || !seriesRef.current || !date) return;

    const fetchVolumeCandles = async () => {
      setVolumeLoading(true);

      try {
        // Use endDate if provided, otherwise default to date (single day)
        const effectiveEndDate = endDate || date;
        const url = `${CONFIG.apiUrl}/api/es/volume-candles?start_date=${date}&volume_size=${volumeSize}&end_date=${effectiveEndDate}`;
        console.log('[ESChart] Fetching volume candles:', url);

        const response = await authFetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('[ESChart] Received volume candles:', result.count);

        if (result.candles && result.candles.length > 0) {
          const chartCandles = result.candles.map(volumeCandleToChartFormat);
          setVolumeCandles(chartCandles);
          seriesRef.current.setData(chartCandles);
          candlesRef.current = chartCandles;

          // Fit content
          requestAnimationFrame(() => {
            chartRef.current?.timeScale().fitContent();
            hasInitialFitRef.current = true;
          });

          setVolumeStats({
            count: result.count,
            totalVolume: result.total_volume,
            finalCvd: result.final_cvd,
          });
        } else {
          seriesRef.current.setData([]);
          setVolumeCandles([]);
          candlesRef.current = [];
          setVolumeStats(null);
        }
      } catch (err) {
        console.error('[ESChart] Volume candle fetch error:', err);
      } finally {
        setVolumeLoading(false);
      }
    };

    fetchVolumeCandles();
  }, [isVolumeChart, chartReady, date, endDate, volumeSize]);

  // Update candle data (for time-based charts - candles passed as prop)
  useEffect(() => {
    // Skip if in volume chart mode - volume candles are fetched separately
    if (isVolumeChart) return;
    if (!seriesRef.current) return;
    if (!candles || candles.length === 0) {
      // Opt-in (renko date nav): clear the series for an empty range instead of
      // leaving the previous range's bars stale on screen. Off by default so the
      // main chart's initial-empty / bulk-via-SSE flow is unchanged.
      if (clearOnEmpty) {
        seriesRef.current.setData([]);
        candlesRef.current = [];
      }
      return;
    }

    console.log('[ESChart] Setting candle data:', candles.length);
    seriesRef.current.setData(displayTypeRef.current === 'line' ? candles.map(toLineData) : candles);
    // Show the current-price line on historical load (market may be closed: no live tick)
    updateCurrentPriceLine();

    // Fit content on initial load or when data range changes significantly.
    // The count threshold (>10) prevents fitContent on live candle ticks
    // but triggers it when switching dates loads a different range.
    const countChanged = Math.abs(candles.length - prevCandleCountRef.current) > 10;
    if (chartRef.current && (!hasInitialFitRef.current || countChanged)) {
      requestAnimationFrame(() => {
        chartRef.current?.timeScale().fitContent();
        hasInitialFitRef.current = true;
      });
    }
    prevCandleCountRef.current = candles.length;

    // Per-candle profile plugin (planning chart): now that candle data is
    // applied and the time scale has a visible range, (re)fetch visible-day
    // profiles. Fixes the initial-load race where enable() ran before any data
    // was in the series (empty visible range -> no fetch). refresh() is a
    // cheap no-op for days already cached, so this is safe on every update.
    if (candleProfilePluginRef.current?.isEnabled()) {
      requestAnimationFrame(() => candleProfilePluginRef.current?.refresh());
    }
  }, [candles, isVolumeChart, updateCurrentPriceLine, clearOnEmpty]);

  // Reset initial fit flag when date changes (switching to a new day)
  useEffect(() => {
    hasInitialFitRef.current = false;
    prevCandleCountRef.current = 0;
  }, [date]);

  // Check if any GEX indicator is enabled (includes OI GEX/DEX for OI-only heatmap and session level indicators)
  const anyGreeksIndicatorEnabled = showOiDistribution || showPrevDayLevels || showPriorWeekLevels || showPriorMonthLevels || showAsiaLevels || showLondonLevels || showOpeningRange || showInitialBalance || showOpeningPrints || showPivotPoints || showZeroLevels || showGammaLevels || showExpectedMove || showProjectedCurves || showExposureProfile || showHedgingDemand || showIvSkew || showOptionsVolume;

  // Initialize session markers plugin separately (independent of GEX indicators)
  useEffect(() => {
    if (!chartReady || !seriesRef.current) return;

    // Create session markers plugin instance if not exists
    if (!sessionMarkersPluginRef.current) {
      sessionMarkersPluginRef.current = new SessionMarkersPlugin();
      console.log('[ESChart] Session markers plugin created');
    }

    return () => {
      // Only cleanup on unmount, not on re-renders
      if (sessionMarkersPluginRef.current && isSessionMarkersAttachedRef.current && seriesRef.current) {
        try {
          seriesRef.current.detachPrimitive(sessionMarkersPluginRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
        isSessionMarkersAttachedRef.current = false;
      }
      sessionMarkersPluginRef.current = null;
    };
  }, [chartReady]); // Only depends on chart readiness

  // Initialize AOI plugin separately (independent of GEX indicators)
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;

    // Create AOI plugin instance if not exists
    if (!orderflowPluginRef.current) {
      orderflowPluginRef.current = new OrderFlowPlugin(chartRef.current, seriesRef.current, {
        apiUrl: CONFIG.apiUrl,
      });
      console.log('[ESChart] AOI plugin created');
    }

    return () => {
      // Only cleanup on unmount, not on re-renders
      if (orderflowPluginRef.current && isOrderflowAttachedRef.current && seriesRef.current) {
        try {
          seriesRef.current.detachPrimitive(orderflowPluginRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
        isOrderflowAttachedRef.current = false;
      }
      orderflowPluginRef.current = null;
    };
  }, [chartReady]); // Only depends on chart readiness

  // Initialize GEX plugin and handle enable/disable when chart is ready
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current || !date) {
      console.log('[ESChart] Not ready:', { chartReady, hasChart: !!chartRef.current, hasSeries: !!seriesRef.current, date });
      return;
    }

    // Create GEX plugin instance if not exists
    if (!greeksPluginRef.current) {
      greeksPluginRef.current = new GreeksHeatmapPlugin(chartRef.current, seriesRef.current, {
        apiUrl: CONFIG.apiUrl,
        timeframe,
        expiryMode,
        underlying,
        showOiDistribution: showOiDistributionRef.current,
        oiDistToggles: { callOi: true, putOi: true, net: true },
        showForwardOi: showForwardOi,             // T+1 session projected curves
        showProjectedCurves: showProjectedCurves,   // Projected curves per session
        projectedCurveGreeks: projectedCurveGreeks,  // Individual curve visibility
        zeroLevelGreeks: zeroLevelGreeks,                // Per-greek OI level visibility
        gammaLevelToggles: gammaLevelToggles,        // Gamma Levels sub-toggle visibility
        showZeroLevelsLive: showZeroLevelsLive,          // Live Flips sub-layer
        projCurvesLive: projCurvesLive,             // Live curve sub-layer (today + scrub)
        onRefitHealth: handleRefitHealth,           // per-horizon refit health intake (state line + pane fade)
        showExposureProfile: showExposureProfileRef.current,  // Exposure Profile at current spot
        expProfileLiveSmile: expProfileLiveSmile,   // Exposure Profile live-smile source
        expProfileToggles: expProfileTogglesRef.current,
        exposureProfileMode: exposureProfileModeRef.current,  // 'dealer' | 'absolute'
        showExpProfileHeatmap: showExpProfileHeatmapRef.current,  // Exposure Profile heatmap sub-layer
        showExpProfileT1: showExpProfileT1Ref.current,  // T+1 Exposure Profile sub-layer
        expProfileAnchorFrac: expProfileAnchorFracRef.current,  // Drag-to-place open-session axis (null = left-edge pin)
        expProfileT1AnchorFrac: expProfileT1AnchorFracRef.current,  // Drag-to-place T+1 axis (null = right-edge pin)
        hedgeDemandAnchorFrac: hedgeDemandAnchorFracRef.current,  // Drag-to-place Hedging Demand axis (null = offset default)
        oiProfileAnchorFrac: oiProfileAnchorFracRef.current,  // Drag-to-place OI distribution axis (null = left-edge pin)
        ivSkewAnchorFrac: ivSkewAnchorFracRef.current,  // Drag-to-place IV Skew axis (null = offset default)
        showOptionsVolume: showOptionsVolumeRef.current,  // Options Volume per-candle histogram
        optVolToggles: optVolTogglesRef.current,          // Options Volume call/put/net sub-toggles
        optVolCumulative: optVolCumulativeRef.current,    // Options Volume cumulative sub-histogram
        optVolSplit: optVolSplitRef.current,              // Options Volume buy/sell split mode
        optVolFlowNet: optVolFlowNetRef.current,          // Options Volume flow net view
        optVolAnchorFrac: optVolAnchorFracRef.current,    // Drag-to-place Options Volume axis (null = offset default)
        optVolCumAnchorFrac: optVolCumAnchorFracRef.current,  // Drag-to-place cumulative axis (null = offset default)
        showHedgingDemand: showHedgingDemandRef.current,  // Per-strike combined hedge flow profile
        showHedgeDemandHeatmap: showHedgeDemandHeatmapRef.current,  // Hedging Demand heatmap sub-layer
        hedgeDemandRealized: hedgeDemandRealizedRef.current,  // Hedging Demand realized mode
        showIvSkew: showIvSkewRef.current,  // IV Skew per-strike call/put IV dots
        ivSkewBand: ivSkewBandRef.current,  // IV Skew derived sub-layer
        ivSkewShapePane: showIvSkewShapeRef.current,  // Smile Shape pane data feed
        onIvSkewShape: (series) => { ivSkewShapeSeriesRef.current = series; ivSkewShapeIndicatorRef.current?.update(series); },
        optVolFlowPane: showVolFlowPaneRef.current,  // Flow pane data feed gate
        onVolFlow: (series) => { volFlowSeriesRef.current = series; volFlowIndicatorRef.current?.update(series); },
        ...CONFIG.greeksHeatmap,
        ...options.greeksHeatmap
      });
      console.log('[ESChart] Plugin initialized - OI Distribution:', showOiDistributionRef.current);
    }

    // Enable/disable plugin based on whether any indicator is on
    let cancelled = false;

    const handlePluginToggle = async () => {
      if (anyGreeksIndicatorEnabled) {
        if (!greeksPluginRef.current.isEnabled()) {
          setGreeksLoading(true);
          try {
            // Sync current toggle state into plugin options before enable() fetches data
            const plugin = greeksPluginRef.current;
            plugin.options.showOiDistribution = showOiDistributionRef.current;
            plugin.options.showHedgingDemand = showHedgingDemandRef.current;
            plugin.options.showExposureProfile = showExposureProfileRef.current;
            plugin.options.expProfileToggles = expProfileTogglesRef.current;
            plugin.options.exposureProfileMode = exposureProfileModeRef.current;
            plugin.options.showExpProfileHeatmap = showExpProfileHeatmapRef.current;
            plugin.options.showExpProfileT1 = showExpProfileT1Ref.current;
            plugin.options.showHedgeDemandHeatmap = showHedgeDemandHeatmapRef.current;
            plugin.options.hedgeDemandRealized = hedgeDemandRealizedRef.current;
            plugin.options.showIvSkew = showIvSkewRef.current;
            plugin.options.ivSkewBand = ivSkewBandRef.current;
            plugin.options.ivSkewShapePane = showIvSkewShapeRef.current;
            plugin.options.optVolFlowPane = showVolFlowPaneRef.current;
            plugin.options.showOptionsVolume = showOptionsVolumeRef.current;
            plugin.options.optVolToggles = optVolTogglesRef.current;
            plugin.options.optVolCumulative = optVolCumulativeRef.current;
            plugin.options.optVolSplit = optVolSplitRef.current;
            plugin.options.optVolFlowNet = optVolFlowNetRef.current;
            plugin.setProfileAnchorFrac('today', expProfileAnchorFracRef.current);
            plugin.setProfileAnchorFrac('t1', expProfileT1AnchorFracRef.current);
            plugin.setProfileAnchorFrac('hedge', hedgeDemandAnchorFracRef.current);
            plugin.setProfileAnchorFrac('oi', oiProfileAnchorFracRef.current);
            plugin.setProfileAnchorFrac('smile', ivSkewAnchorFracRef.current);
            plugin.setProfileAnchorFrac('vol', optVolAnchorFracRef.current);
            plugin.setProfileAnchorFrac('volcum', optVolCumAnchorFracRef.current);
            plugin.options.timeframe = timeframe;
            plugin.options.expiryMode = expiryMode;
            plugin.options.underlying = underlying;
            console.log('[ESChart] Enabling GEX plugin for date:', date, 'OI Distribution:', plugin.options.showOiDistribution);
            await plugin.enable(date);
            // If this effect was cleaned up during the await, don't touch state
            if (cancelled) return;
            // Set initial visibility states
            greeksPluginRef.current.setShowHedgingDemand(showHedgingDemand);
            // Set session level visibility states
            greeksPluginRef.current.setShowPrevDayLevels(showPrevDayLevels);
            greeksPluginRef.current.setShowPriorWeekLevels(showPriorWeekLevels);
            greeksPluginRef.current.setShowPriorMonthLevels(showPriorMonthLevels);
            greeksPluginRef.current.setShowAsiaLevels(showAsiaLevels);
            greeksPluginRef.current.setShowLondonLevels(showLondonLevels);
            greeksPluginRef.current.setShowOpeningRange(showOpeningRange);
            greeksPluginRef.current.setShowInitialBalance(showInitialBalance);
            greeksPluginRef.current.setShowOpeningPrints(showOpeningPrints);
            greeksPluginRef.current.setShowPivotPoints(showPivotPoints);
            greeksPluginRef.current.setShowZeroLevels(showZeroLevels);
            greeksPluginRef.current.setShowGammaLevels(showGammaLevels);
            greeksPluginRef.current.setGammaLevelToggles(gammaLevelToggles);
            greeksPluginRef.current.setShowZeroLevelsLive(showZeroLevelsLive);
            greeksPluginRef.current.setProjCurvesLive(projCurvesLive);
            greeksPluginRef.current.setShowExpectedMove(showExpectedMove);
            // Push existing level data to the plugin if available
            // This ensures Zero Levels render even if levelData arrived before plugin was enabled
            // Use ref to avoid stale closure - levelData prop may have been updated
            // by useLevelIndicators while the async enable() was in flight
            if (levelDataRef.current) {
              greeksPluginRef.current.setLevelData(levelDataRef.current);
            }
            console.log('[ESChart] GEX plugin enabled with session levels - Zero Levels:', showZeroLevels);
          } catch (err) {
            if (cancelled) return;
            console.error('[ESChart] GEX enable error:', err);
          } finally {
            if (!cancelled) setGreeksLoading(false);
          }
        }
      } else {
        if (greeksPluginRef.current) {
          greeksPluginRef.current.disable();
        }
        console.log('[ESChart] GEX plugin disabled (no indicators enabled)');
      }
    };

    handlePluginToggle();

    return () => {
      cancelled = true;
      if (greeksPluginRef.current) {
        greeksPluginRef.current.disable();
        greeksPluginRef.current = null;
      }
      // Note: Session markers cleanup is handled in its own effect
    };
  }, [chartReady, anyGreeksIndicatorEnabled, date, timeframe, expiryMode, underlying]);

  // Handle Hedging Demand visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current && greeksPluginRef.current.isEnabled()) {
      greeksPluginRef.current.setShowHedgingDemand(showHedgingDemand);
    }
  }, [showHedgingDemand]);

  // Handle Hedging Demand heatmap sub-layer toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowHedgeDemandHeatmap(showHedgeDemandHeatmap);
    }
  }, [showHedgeDemandHeatmap]);

  // Handle IV Skew toggle (may fetch smile rows if not yet loaded)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowIvSkew(showIvSkew);
    }
  }, [showIvSkew]);

  // Handle Options Volume toggle (may fetch volume rows if not yet loaded)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowOptionsVolume(showOptionsVolume);
    }
  }, [showOptionsVolume]);

  // Options Volume call/put/net sub-toggles (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setOptVolToggles(optVolToggles);
    }
  }, [optVolToggles]);

  // Options Volume cumulative sub-histogram toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setOptVolCumulative(optVolCumulative);
    }
  }, [optVolCumulative]);

  // Options Volume buy/sell split toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setOptVolSplit(optVolSplit);
    }
  }, [optVolSplit]);

  // Options Volume flow net toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setOptVolFlowNet(optVolFlowNet);
    }
  }, [optVolFlowNet]);

  // IV Skew derived sub-layers (render-only: same fetched rows, new draw)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (!plugin) return;
    plugin.options.ivSkewBand = ivSkewBand;
    if (plugin.isEnabled()) plugin.draw();
  }, [ivSkewBand]);

  // Smile Shape pane data-feed toggle on the plugin (lazy smile fetch
  // when the dots layer is off)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setIvSkewShapePane(showIvSkewShape);
    }
  }, [showIvSkewShape]);

  // Flow pane data-feed toggle on the plugin (re-emits from the
  // already-fetched volume rows on toggle-on)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setVolFlowPane(showVolFlowPane);
    }
  }, [showVolFlowPane]);


  // Handle Hedging Demand realized mode toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setHedgeDemandRealized(hedgeDemandRealized);
    }
  }, [hedgeDemandRealized]);

  // Handle T+1 forward OI toggle (render-only, no data refetch)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowForwardOi(showForwardOi);
    }
  }, [showForwardOi]);

  // Handle Projected Curves toggle (may fetch OI data if not yet loaded)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowProjectedCurves(showProjectedCurves).then(() => {
        if (plugin.isEnabled()) {
        }
      });
    }
  }, [showProjectedCurves]);

  // Handle individual projected curve Greek toggles
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProjectedCurveGreeks(projectedCurveGreeks);
    }
  }, [projectedCurveGreeks]);

  // Handle individual OI level Greek toggles
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setZeroLevelGreeks(zeroLevelGreeks);
    }
  }, [zeroLevelGreeks]);

  // Handle Gamma Levels sub-toggles
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setGammaLevelToggles(gammaLevelToggles);
    }
  }, [gammaLevelToggles]);

  // Handle Exposure Profile toggle (may fetch exposure rows if not yet loaded)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowExposureProfile(showExposureProfile);
    }
  }, [showExposureProfile]);

  // Handle Exposure Profile weighting mode change (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setExposureProfileMode(exposureProfileMode);
    }
  }, [exposureProfileMode]);

  // Handle Exposure Profile greek sub-toggles
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setExpProfileToggles(expProfileToggles);
    }
  }, [expProfileToggles]);

  // Handle Exposure Profile heatmap sub-toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowExpProfileHeatmap(showExpProfileHeatmap);
    }
  }, [showExpProfileHeatmap]);

  // Handle T+1 Exposure Profile sub-toggle (render-only)
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setShowExpProfileT1(showExpProfileT1);
    }
  }, [showExpProfileT1]);

  // Handle drag-to-place profile anchor changes (render-only). During a drag
  // the pointer handlers call setProfileAnchorFrac directly; these effects
  // cover programmatic changes (reset, cross-page state) and are no-ops when
  // the value already matches.
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProfileAnchorFrac('today', expProfileAnchorFrac);
    }
  }, [expProfileAnchorFrac]);

  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProfileAnchorFrac('t1', expProfileT1AnchorFrac);
    }
  }, [expProfileT1AnchorFrac]);

  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProfileAnchorFrac('hedge', hedgeDemandAnchorFrac);
    }
  }, [hedgeDemandAnchorFrac]);

  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProfileAnchorFrac('oi', oiProfileAnchorFrac);
    }
  }, [oiProfileAnchorFrac]);

  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProfileAnchorFrac('vol', optVolAnchorFrac);
    }
  }, [optVolAnchorFrac]);

  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setProfileAnchorFrac('volcum', optVolCumAnchorFrac);
    }
  }, [optVolCumAnchorFrac]);

  // Drag-to-place interaction for the Exposure Profile / Hedging Demand and
  // T+1 profile zero axes. CAPTURE-phase listeners on the chart container:
  // lightweight-charts attaches its own pan handlers to the pane canvases
  // (children of this container), so a bubble-phase listener would fire
  // AFTER the chart already started a pan for the gesture. On an axis hit
  // the pointerdown is consumed (preventDefault + stopPropagation) so the
  // chart never sees it; pan/scale are also disabled for the drag as
  // defense-in-depth and restored on release. Non-hit events pass through
  // untouched. Position persists via onProfileAnchorChange on release only.
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const HIT_TOLERANCE_PX = 6;
    const HIT_Y_SLACK_PX = 10;
    const ANCHOR_KIND_TO_ID = {
      today: 'expProfileAnchorFrac',
      t1: 'expProfileT1AnchorFrac',
      hedge: 'hedgeDemandAnchorFrac',
      oi: 'oiProfileAnchorFrac',
      smile: 'ivSkewAnchorFrac',
      vol: 'optVolAnchorFrac',
      volcum: 'optVolCumAnchorFrac',
    };
    const drag = { kind: null, frac: null, moved: false, startX: 0, startY: 0, lastUp: null };

    const paneCoords = (e) => {
      const rect = container.getBoundingClientRect();
      let leftScaleWidth = 0;
      try {
        leftScaleWidth = chartRef.current?.priceScale('left').width() || 0;
      } catch { /* left scale absent */ }
      return { x: e.clientX - rect.left - leftScaleWidth, y: e.clientY - rect.top };
    };

    const hitAnchor = (pt) => {
      const hits = greeksPluginRef.current?.getAnchorHit?.();
      if (!hits) return null;
      for (const kind of ['today', 't1', 'hedge', 'oi', 'smile', 'vol', 'volcum']) {
        const hit = hits[kind];
        if (!hit) continue;
        if (Math.abs(pt.x - hit.x) <= HIT_TOLERANCE_PX
            && pt.y >= hit.minY - HIT_Y_SLACK_PX && pt.y <= hit.maxY + HIT_Y_SLACK_PX) {
          return kind;
        }
      }
      return null;
    };

    const setPanEnabled = (enabled) => {
      const chart = chartRef.current;
      if (!chart) return;
      try {
        chart.applyOptions(enabled ? {
          handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: true },
          handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
        } : { handleScroll: false, handleScale: false });
      } catch { /* chart disposed */ }
    };

    const endDrag = (e) => {
      if (drag.kind === null) return;
      const { kind, frac, moved } = drag;
      drag.kind = null;
      drag.frac = null;
      try { container.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
      setPanEnabled(true);
      const notify = onProfileAnchorChangeRef.current;
      if (moved) {
        // Real drag: persist the final position once, on release only.
        drag.lastUp = null;
        if (frac != null && notify) notify(ANCHOR_KIND_TO_ID[kind], frac);
        return;
      }
      // Click without movement. Two clicks on the same axis within 400ms and
      // ~4px = reset to the default pin. Detected manually: pointer capture
      // retargets events, so native click/dblclick composition is unreliable
      // here.
      const now = performance.now();
      const isReset = e.type === 'pointerup' && drag.lastUp
        && drag.lastUp.kind === kind && (now - drag.lastUp.t) < 400
        && Math.abs(e.clientX - drag.lastUp.x) < 4 && Math.abs(e.clientY - drag.lastUp.y) < 4;
      if (isReset) {
        drag.lastUp = null;
        greeksPluginRef.current?.setProfileAnchorFrac(kind, null);
        if (notify) notify(ANCHOR_KIND_TO_ID[kind], null);
        return;
      }
      drag.lastUp = e.type === 'pointerup' ? { kind, t: now, x: e.clientX, y: e.clientY } : null;
    };

    const onPointerDown = (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      const kind = hitAnchor(paneCoords(e));
      if (!kind) return;
      e.preventDefault();
      e.stopPropagation();
      drag.kind = kind;
      drag.frac = null;
      drag.moved = false;
      drag.startX = e.clientX;
      drag.startY = e.clientY;
      try { container.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
      setPanEnabled(false);
    };

    const onPointerMove = (e) => {
      if (drag.kind === null) {
        container.style.cursor = hitAnchor(paneCoords(e)) ? 'ew-resize' : '';
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (Math.abs(e.clientX - drag.startX) > 3 || Math.abs(e.clientY - drag.startY) > 3) {
        drag.moved = true;
      }
      if (!drag.moved) return;
      const paneWidth = chartRef.current?.timeScale().width() || 0;
      if (paneWidth <= 0) return;
      const frac = Math.min(0.97, Math.max(0.03, paneCoords(e).x / paneWidth));
      drag.frac = frac;
      greeksPluginRef.current?.setProfileAnchorFrac(drag.kind, frac);
    };

    const onPointerUp = (e) => {
      if (drag.kind === null) return;
      e.preventDefault();
      e.stopPropagation();
      endDrag(e);
    };

    const onPointerCancel = (e) => {
      endDrag(e);
    };

    container.addEventListener('pointerdown', onPointerDown, { capture: true });
    container.addEventListener('pointermove', onPointerMove, { capture: true });
    container.addEventListener('pointerup', onPointerUp, { capture: true });
    container.addEventListener('pointercancel', onPointerCancel, { capture: true });
    return () => {
      container.removeEventListener('pointerdown', onPointerDown, { capture: true });
      container.removeEventListener('pointermove', onPointerMove, { capture: true });
      container.removeEventListener('pointerup', onPointerUp, { capture: true });
      container.removeEventListener('pointercancel', onPointerCancel, { capture: true });
      container.style.cursor = '';
      if (drag.kind !== null) setPanEnabled(true);
    };
  }, []);

  // Handle OI distribution sub-toggles
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setOiDistToggles(oiDistToggles);
    }
  }, [oiDistToggles]);


  // Handle Previous Day Levels visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowPrevDayLevels(showPrevDayLevels);
      console.log('[ESChart] Previous Day Levels visibility toggled:', showPrevDayLevels);
    }
  }, [showPrevDayLevels]);

  // Handle Prior Week Levels visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowPriorWeekLevels(showPriorWeekLevels);
    }
  }, [showPriorWeekLevels]);

  // Handle Prior Month Levels visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowPriorMonthLevels(showPriorMonthLevels);
    }
  }, [showPriorMonthLevels]);

  // Handle Asia Session Levels visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowAsiaLevels(showAsiaLevels);
    }
  }, [showAsiaLevels]);

  // Handle London Session Levels visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowLondonLevels(showLondonLevels);
    }
  }, [showLondonLevels]);

  // Handle Opening Range visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowOpeningRange(showOpeningRange);
      console.log('[ESChart] Opening Range visibility toggled:', showOpeningRange);
    }
  }, [showOpeningRange]);

  // Handle Initial Balance visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowInitialBalance(showInitialBalance);
      console.log('[ESChart] Initial Balance visibility toggled:', showInitialBalance);
    }
  }, [showInitialBalance]);

  // Handle Opening Prints visibility toggle (GOP/RTHOP)
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowOpeningPrints(showOpeningPrints);
    }
  }, [showOpeningPrints]);

  // Handle Pivot Points visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowPivotPoints(showPivotPoints);
    }
  }, [showPivotPoints]);

  // Handle Zero Levels visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowZeroLevels(showZeroLevels);
      console.log('[ESChart] Zero Levels visibility toggled:', showZeroLevels);
    }
  }, [showZeroLevels]);

  // Handle Gamma Levels visibility toggle (at-spot band, walls)
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowGammaLevels(showGammaLevels);
    }
  }, [showGammaLevels]);

  // Handle Live Flips sub-toggle (per-minute live-book crossings)
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowZeroLevelsLive(showZeroLevelsLive);
    }
  }, [showZeroLevelsLive]);

  // Handle Projected Curves Live sub-toggle (live today-curve + cursor scrub)
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setProjCurvesLive(projCurvesLive);
    }
  }, [projCurvesLive]);

  // Handle Exposure Profile live-smile source sub-toggle
  useEffect(() => {
    const plugin = greeksPluginRef.current;
    if (plugin && plugin.isEnabled()) {
      plugin.setExpProfileLiveSmile(expProfileLiveSmile);
    }
  }, [expProfileLiveSmile]);

  // Handle Expected Move visibility toggle
  useEffect(() => {
    if (greeksPluginRef.current) {
      greeksPluginRef.current.setShowExpectedMove(showExpectedMove);
    }
  }, [showExpectedMove]);

  // Handle OI GEX/DEX toggle - triggers data re-fetch with new OI settings
  // Note: showOiDistributionRef is updated synchronously at top of component
  // Skip first render since plugin initialization handles initial OI values
  useEffect(() => {
    // Skip on initial mount - plugin initialization handles initial OI values
    if (isFirstOiRenderRef.current) {
      isFirstOiRenderRef.current = false;
      return;
    }

    // Only handle OI changes if plugin exists and is enabled
    const plugin = greeksPluginRef.current;
    if (!plugin || !plugin.isEnabled()) {
      console.log('[ESChart] OI toggle changed but plugin not ready');
      return;
    }

    const handleOiChange = async () => {
      console.log('[ESChart] OI toggle changed - Distribution:', showOiDistribution);
      if (onBaselineLoadingChange) {
        onBaselineLoadingChange(true);
      }
      try {
        await plugin.setOiSettings({ showOiDistribution }, date);
        if (!plugin.isEnabled()) return;
      } catch (err) {
        console.error('[ESChart] OI toggle error:', err);
      } finally {
        if (onBaselineLoadingChange) {
          onBaselineLoadingChange(false);
        }
      }
    };
    handleOiChange();
  }, [showOiDistribution, date, onBaselineLoadingChange]);

  // Handle Session Markers visibility toggle
  useEffect(() => {
    if (!seriesRef.current || !sessionMarkersPluginRef.current) return;

    if (showSessionMarkers && !isSessionMarkersAttachedRef.current) {
      try {
        seriesRef.current.attachPrimitive(sessionMarkersPluginRef.current);
        isSessionMarkersAttachedRef.current = true;
        console.log('[ESChart] Session markers attached');
      } catch (e) {
        console.error('[ESChart] Error attaching session markers:', e);
      }
    } else if (!showSessionMarkers && isSessionMarkersAttachedRef.current) {
      try {
        seriesRef.current.detachPrimitive(sessionMarkersPluginRef.current);
        isSessionMarkersAttachedRef.current = false;
        // Force chart redraw
        if (chartRef.current) {
          chartRef.current.priceScale('right').applyOptions({});
        }
        console.log('[ESChart] Session markers detached');
      } catch (e) {
        console.error('[ESChart] Error detaching session markers:', e);
      }
    }
  }, [showSessionMarkers]);

  // Handle order-flow visibility toggles
  useEffect(() => {
    if (!seriesRef.current || !orderflowPluginRef.current) return;

    // Use volume candles when in volume chart mode, otherwise use time-based candles
    const activeCandles = isVolumeChart ? volumeCandles : candles;

    // Attach plugin if not attached and any order-flow feature is enabled
    const anyOrderflowEnabled =
      showOrderflowAbsorption || showOrderflowAggressiveOrders ||
      showOrderflowContextProfile ||
      showOrderflowStructure || showOrderflowRotationLegs || showOrderflowRotationReach;

    if (anyOrderflowEnabled && !isOrderflowAttachedRef.current) {
      try {
        seriesRef.current.attachPrimitive(orderflowPluginRef.current);
        isOrderflowAttachedRef.current = true;
        console.log('[ESChart] AOI plugin attached');
      } catch (e) {
        console.error('[ESChart] Error attaching AOI plugin:', e);
      }
    }

    // Update candles in plugin for zone snapping (handles timeframe changes)
    if (anyOrderflowEnabled && activeCandles && activeCandles.length > 0) {
      orderflowPluginRef.current.updateCandles(activeCandles);
    }

    // Order-flow level families are served from the single live snapshot (/snapshot) + the
    // orderflow SSE; they carry start times, so no date range is passed (one live session).
    if (showOrderflowAbsorption) {
      orderflowPluginRef.current.enableAbsorption();
    } else {
      orderflowPluginRef.current.disableAbsorption();
    }

    if (showOrderflowAggressiveOrders) {
      orderflowPluginRef.current.enableFocusLevels();
    } else {
      orderflowPluginRef.current.disableFocusLevels();
    }

    // Context profile overlay (prior-session profile fills + POC/value/core lines)
    if (showOrderflowContextProfile) {
      orderflowPluginRef.current.enableContextProfile();
    } else {
      orderflowPluginRef.current.disableContextProfile();
    }

    // Market structure (tier-1/2 pivots + BOS/CHoCH break lines + bias chips; descriptive)
    if (showOrderflowStructure) {
      orderflowPluginRef.current.enableMarketStructure();
      orderflowPluginRef.current.setStructShowDetail(showOrderflowStructureDetail);
    } else {
      orderflowPluginRef.current.disableMarketStructure();
    }

    // Renko rotations request by ET SESSION date, NOT the candle UTC range: the ES session opens
    // 18:00 ET = 22:00 UTC the prior calendar day, so getDateRangeFromCandles (UTC) straddles
    // midnight and yields two dates, dragging the prior session's legs/anchor into the overlay and
    // mis-aligning it with the /chart/renko page (single session). For the current session send no
    // date (omit start_date => backend default canonical path = renko page + live SSE); for a
    // historic `date` send it as start (backend spans date..today, matching the renko historic view).
    const rotationStart = (date === getLatestSessionDate()) ? null : date;

    // Renko rotation legs (zig-zag connecting pivots)
    if (showOrderflowRotationLegs) {
      orderflowPluginRef.current.enableRotationLegs(rotationStart, null, activeCandles, rotationBrickSize);
    } else {
      orderflowPluginRef.current.disableRotationLegs();
    }

    // Renko rotation reach levels (p50/p70/p90 per pivot + forward projection)
    if (showOrderflowRotationReach) {
      orderflowPluginRef.current.enableRotationReach(rotationStart, null, activeCandles, rotationBrickSize);
    } else {
      orderflowPluginRef.current.disableRotationReach();
    }

    // Detach plugin if all AOI features are disabled
    if (!anyOrderflowEnabled && isOrderflowAttachedRef.current) {
      try {
        orderflowPluginRef.current.disable();
        seriesRef.current.detachPrimitive(orderflowPluginRef.current);
        isOrderflowAttachedRef.current = false;
        // Force chart redraw
        if (chartRef.current) {
          chartRef.current.priceScale('right').applyOptions({});
        }
        console.log('[ESChart] AOI plugin detached');
      } catch (e) {
        console.error('[ESChart] Error detaching AOI plugin:', e);
      }
    }
  }, [showOrderflowAbsorption, showOrderflowAggressiveOrders, showOrderflowContextProfile, showOrderflowStructure, showOrderflowStructureDetail, showOrderflowRotationLegs, showOrderflowRotationReach, rotationBrickSize, date, candles, volumeCandles, isVolumeChart]);

  // Create VWAP indicator instance once when chart is ready
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;

    if (!vwapIndicatorRef.current) {
      vwapIndicatorRef.current = new VWAPIndicator(chartRef.current, seriesRef.current);
      // Apply persisted session mode before any enable() fetch
      vwapIndicatorRef.current.sessionMode = vwapSessionMode === 'split' ? 'split' : 'full';
      console.log('[ESChart] VWAP indicator created');
    }

    return () => {
      if (vwapIndicatorRef.current) {
        vwapIndicatorRef.current.disable();
        vwapIndicatorRef.current = null;
      }
    };
  }, [chartReady]);

  // Handle IV indicator toggle (mirrors CVD pattern)
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;

    if (!quoteIVIndicatorRef.current) {
      quoteIVIndicatorRef.current = new QuoteIVIndicator(chartRef.current, underlying);
      quoteIVIndicatorRef.current.setTimeframe(timeframe);
      quoteIVIndicatorRef.current.setStdDev(showIVStdDev);
      quoteIVIndicatorRef.current.setShowRealized(showIVRealized);
      console.log('[ESChart] Quote IV indicator created');
    }

    if (showIV) {
      quoteIVIndicatorRef.current.enable(ivDataRef.current);
      quoteIVIndicatorRef.current.setRealized(rvSegmentsRef.current);
    } else {
      quoteIVIndicatorRef.current.disable();
    }

    return () => {
      if (quoteIVIndicatorRef.current) {
        quoteIVIndicatorRef.current.disable();
        quoteIVIndicatorRef.current = null;
      }
    };
    // underlying in the deps = teardown-recreate on switch (the cleanup nulls
    // the ref), so the SSE filter and series state follow the page's selection.
  }, [chartReady, showIV, underlying]);

  // Memoize VWAP date range to avoid recalculating on every candle update
  // Only recalculates when the first or last candle timestamp changes (i.e., date range changes)
  const vwapDateRange = useMemo(() => {
    if (!candles || candles.length === 0) {
      return { startDate: date, endDate: date };
    }

    const firstTs = candles[0].time;
    const lastTs = candles[candles.length - 1].time;

    // Convert to trading day - candles at/after Globex open (6 PM ET) belong
    // to the NEXT trading day. Must be computed in ET (DST-safe): the old
    // utcHour >= 23 check was only correct in EST and made the first evening
    // candle map to the previous day in EDT, fetching VWAP for a day whose
    // candles aren't loaded.
    const getTradingDay = (timestamp) => {
      const d = new Date(timestamp * 1000);
      const etDate = d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      const etHour = Number(d.toLocaleString('en-US', {
        timeZone: 'America/New_York', hour: '2-digit', hourCycle: 'h23',
      }));
      if (etHour >= 18) {
        const next = new Date(`${etDate}T12:00:00Z`);
        next.setUTCDate(next.getUTCDate() + 1);
        return next.toISOString().split('T')[0];
      }
      return etDate;
    };

    return {
      startDate: getTradingDay(firstTs),
      endDate: getTradingDay(lastTs)
    };
  }, [candles?.[0]?.time, candles?.[candles?.length - 1]?.time, date]);

  // Fetch IV data when date range changes (mirrors CVD pattern). Also
  // fetched when the Hedging Demand realized mode needs the ATM IV series
  // while the IV Index line itself is off (the line still only draws
  // under showIV; realized HD reads the segments via the GEX plugin).
  useEffect(() => {
    // Realized HD never consumes IV on the ES underlying (its quote-IV
    // series is unusable for dIV; the plugin resolver is guarded), so only
    // the IV Index line itself justifies the fetch there.
    const realizedNeedsIv = hedgeDemandRealized && underlying !== 'es';
    if (!showIV && !realizedNeedsIv) return;
    const { startDate, endDate } = vwapDateRange;

    let cancelled = false;
    const fetchIV = async () => {
      try {
        const url = `${CONFIG.apiUrl}/api/indicators/quote-iv?start_date=${startDate}&end_date=${endDate}&timeframe=${timeframe}&underlying=${underlying}`;
        console.log('[ESChart] Fetching IV data:', startDate, 'to', endDate);
        const response = await authFetch(url);
        if (!response.ok || cancelled) return;

        const result = await response.json();
        if (cancelled) return;

        // Build per-day segments (one array per day) so the indicator
        // creates separate series and lines don't connect across overnight gaps
        const segments = [];
        if (result.iv_index?.length > 0) {
          const dayGroups = new Map();
          for (const p of result.iv_index) {
            const etDate = new Date(p.time * 1000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
            if (!dayGroups.has(etDate)) dayGroups.set(etDate, []);
            dayGroups.get(etDate).push({ time: p.time, value: p.atm_iv });
          }
          for (const [, dayPoints] of [...dayGroups.entries()].sort()) {
            dayPoints.sort((a, b) => a.time - b.time);
            segments.push(dayPoints);
          }
        }

        ivDataRef.current = segments;
        setIvData(segments);
        const totalPoints = segments.reduce((sum, s) => sum + s.length, 0);
        console.log(`[ESChart] IV data loaded: ${totalPoints} points across ${segments.length} days`);
      } catch (error) {
        console.error('[ESChart] Error fetching IV data:', error);
      }
    };

    fetchIV();
    return () => { cancelled = true; };
    // underlying in the deps so switching the page's underlying refetches the
    // matching IV history (es maps to the ES streamer's own series).
  }, [showIV, hedgeDemandRealized, vwapDateRange, timeframe, underlying]);

  // Update IV indicator when data changes (mirrors CVD pattern)
  useEffect(() => {
    if (quoteIVIndicatorRef.current && showIV) {
      quoteIVIndicatorRef.current.update(ivData);
    }
    // Feed the same segments to the GEX plugin for realized Hedging Demand
    // (cheap no-op when the mode is off; the plugin keeps its own copy).
    greeksPluginRef.current?.setQuoteIvSegments?.(ivData);
  }, [ivData, showIV]);

  // Update IV timeframe when it changes
  useEffect(() => {
    if (quoteIVIndicatorRef.current) {
      quoteIVIndicatorRef.current.setTimeframe(timeframe);
    }
  }, [timeframe]);

  // Toggle IV std dev bands
  useEffect(() => {
    if (quoteIVIndicatorRef.current) {
      quoteIVIndicatorRef.current.setStdDev(showIVStdDev);
    }
  }, [showIVStdDev]);

  // Realized-vol companion toggle
  useEffect(() => {
    quoteIVIndicatorRef.current?.setShowRealized(showIVRealized);
  }, [showIVRealized]);

  // Realized-vol series: trailing ~2h WALL-CLOCK window (rvWindowBars) of
  // zero-mean close-to-close log returns per session segment, plus the
  // pooled session-open gap-variance component (weekend + maintenance
  // halts; see computeRvSigma), annualized with the SAME calendar-year
  // convention the backend tau uses (src/constants.py SECONDS_PER_YEAR
  // = 365.25*24*3600). FIXED estimator choices: documented, not tunable.
  // Descriptive comparison of delivered vs priced movement; no signal
  // claim. Live continuation happens per completed bar in updateCandle;
  // this effect rebuilds history and reconciles the live state.
  useEffect(() => {
    if (!showIV || !showIVRealized || isVolumeChart) return;
    if (!candles || candles.length < 6) return;
    const tfSec = EXPOSURE_TF_SECONDS[timeframe] || 300;
    const windowBars = rvWindowBars(tfSec);
    // Segment on session boundaries; each segment snapshots the gap pool
    // known at its open (causal: early history has fewer gaps banked).
    const segs = [];  // { returns: [{time, ret}], gaps: [...] }
    const gapPool = [];
    let cur = null;
    let prev = null;
    for (const c of candles) {
      const close = c.close ?? c.value;
      if (!(close > 0)) continue;
      if (prev && c.time - prev.time > tfSec * 3) {
        const open = c.open ?? close;
        if (open > 0) {
          gapPool.push(Math.log(open / prev.close));
          if (gapPool.length > RV_GAP_POOL) gapPool.shift();
        }
        cur = null;
        prev = null;
      }
      if (!cur) {
        cur = { returns: [], gaps: gapPool.slice() };
        segs.push(cur);
      }
      if (prev) cur.returns.push({ time: c.time, ret: Math.log(close / prev.close) });
      prev = { time: c.time, close };
    }
    const out = segs
      .map((seg) => {
        const pts = [];
        for (let i = 4; i < seg.returns.length; i++) {
          const start = Math.max(0, i - windowBars + 1);
          const w = seg.returns.slice(start, i + 1).map((x) => x.ret);
          const sigma = computeRvSigma(w, seg.gaps, tfSec);
          if (sigma != null && isFinite(sigma)) {
            pts.push({ time: seg.returns[i].time, value: sigma });
          }
        }
        return pts;
      })
      .filter((s) => s.length);
    rvSegmentsRef.current = out;
    // setRealized stores these SAME array references; the live path in
    // updateCandle mutates them in place so redraws stay current.
    quoteIVIndicatorRef.current?.setRealized(out);
    // Reconcile the live RV state: reseed from the prop tail unless the
    // live state is already AHEAD of it (e.g. this effect re-ran on a
    // toggle with a stale prop); in that case keep the live state and
    // start the next appended point in a fresh segment so the off-window
    // void renders as a line break, not a false connection.
    const st = rvLiveStateRef.current;
    const lastSeg = segs.length ? segs[segs.length - 1] : null;
    if (!st || st.tfSec !== tfSec || st.lastTime === null || !prev || prev.time >= st.lastTime) {
      rvLiveStateRef.current = {
        tfSec,
        lastTime: prev ? prev.time : null,
        lastClose: prev ? prev.close : null,
        returns: lastSeg ? lastSeg.returns.slice(-windowBars).map((x) => x.ret) : [],
        gaps: gapPool.slice(),
        pendingNewSegment: false,
      };
    } else {
      st.pendingNewSegment = true;
    }
  }, [candles, timeframe, showIV, showIVRealized, isVolumeChart]);

  // Handle real SPX/SPY quote line toggles (mirrors the IV pattern; one
  // indicator instance per underlying, each on its own overlay price scale)
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;

    // Time-based charts only: volume-candle timestamps are irregular, so
    // minute-grid quote candles would insert extra time slots
    for (const key of OVERLAY_KEYS) {
      if (!quoteIndicatorRefs.current[key]) {
        quoteIndicatorRefs.current[key] =
          new UnderlyingQuoteIndicator(chartRef.current, key);
        quoteIndicatorRefs.current[key].setTimeframe(timeframe);
      }
      if (quoteOverlays[key] && !isVolumeChart) {
        quoteIndicatorRefs.current[key].enable(underlyingQuoteDataRef.current[key]);
      } else {
        quoteIndicatorRefs.current[key].disable();
      }
    }

    return () => {
      for (const key of OVERLAY_KEYS) {
        const ind = quoteIndicatorRefs.current[key];
        if (ind) {
          ind.disable();
          quoteIndicatorRefs.current[key] = null;
        }
      }
    };
  }, [chartReady, quoteOverlays, isVolumeChart]);

  // Fetch enabled overlays' 1m closes when the date range or toggle set
  // changes. The underlyings= filter keeps the payload to ONLY enabled
  // overlays (a full-Globex root is ~1380 bars/day vs 390 for RTH cash).
  useEffect(() => {
    const wanted = OVERLAY_KEYS.filter((k) => quoteOverlays[k]);
    if (wanted.length === 0) return;
    const { startDate, endDate } = vwapDateRange;

    let cancelled = false;
    const fetchQuotes = async () => {
      try {
        // rv=1: meta envelope (alignment); busts 24h-cached historical bodies
        const url = `${CONFIG.apiUrl}/api/greeks/underlying-candles?start_date=${startDate}&end_date=${endDate}&underlyings=${wanted.join(',')}&rv=1`;
        const response = await authFetch(url);
        if (!response.ok || cancelled) return;

        const result = await response.json();
        if (cancelled || !result?.days) return;

        // Session anchor = the per-day T-1 ratio (same walk-back as the
        // tooltip). The SAME ratio places the exposure/OI levels, so the
        // rebased candles share their geometry exactly (anchor error
        // cancels between candle and level). Never the live ratio.
        // Returns {anchor, anchorEs} from the SAME walked-back row:
        // anchorEs = the vix anchor's ES T-1 close numerator ('vix_es'),
        // consumed by gain-compressed overlays; null (uncompressed
        // fallback) for the others and for days without the companion.
        const resolveAnchor = (day, underlying) => {
          const ratioMap = sessionRatiosRef.current;
          let key = day;
          for (let i = 0; i < 6 && key; i++) {
            const r = ratioMap[key];
            if (r && r[underlying] != null) {
              return {
                anchor: r[underlying],
                anchorEs: underlying === 'vix' ? (r.vix_es ?? null) : null,
              };
            }
            const d = new Date(key + 'T12:00:00');
            do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
            key = d.toISOString().split('T')[0];
          }
          return { anchor: null, anchorEs: null };
        };

        // Per-day segments of raw 1m bars + session anchor per underlying
        // (the indicator rebases and aggregates to the chart timeframe);
        // one segment per day so candles never connect across overnight gaps
        const segments = Object.fromEntries(OVERLAY_KEYS.map((k) => [k, []]));
        for (const day of Object.keys(result.days).sort()) {
          for (const underlying of OVERLAY_KEYS) {
            const bars = result.days[day][underlying];
            if (bars?.length > 0) {
              const { anchor, anchorEs } = resolveAnchor(day, underlying);
              segments[underlying].push({ date: day, bars, anchor, anchorEs });
            }
          }
        }

        underlyingQuoteDataRef.current = segments;
        setUnderlyingQuoteData(segments);
      } catch (error) {
        console.error('[ESChart] Error fetching underlying quotes:', error);
      }
    };

    fetchQuotes();
    return () => { cancelled = true; };
  }, [quoteOverlays, vwapDateRange, ratiosVersion]);

  // Push refetched quote data into the enabled indicators
  useEffect(() => {
    for (const key of OVERLAY_KEYS) {
      const ind = quoteIndicatorRefs.current[key];
      if (ind && quoteOverlays[key]) {
        ind.update(underlyingQuoteData[key]);
      }
    }
  }, [underlyingQuoteData, quoteOverlays]);

  // Keep quote-line bucketing aligned with the chart timeframe
  useEffect(() => {
    for (const key of OVERLAY_KEYS) {
      const ind = quoteIndicatorRefs.current[key];
      if (ind) ind.setTimeframe(timeframe);
    }
  }, [timeframe]);

  // Handle VWAP indicator visibility toggle and updates
  // Only runs when date range actually changes (not on every candle update)
  // Real-time updates for current day are handled by VWAPIndicator's SSE connection
  useEffect(() => {
    if (!chartReady || !vwapIndicatorRef.current) return;

    const handleVwap = async () => {
      const indicator = vwapIndicatorRef.current;
      const isEnabled = indicator.isEnabled();
      const { startDate, endDate } = vwapDateRange;

      if (showVWAP) {
        // Renko: align VWAP onto the brick slots before the first draw so the VWAP
        // time set stays a subset of the brick times (otherwise the bricks space out).
        // Inert (null) on every other chart.
        indicator.setRenkoSlots(isRenko ? (candlesPropRef.current || []).map(c => c.time) : null);
        if (!isEnabled) {
          // First time enabling - use enable()
          console.log('[ESChart] Enabling VWAP for date range:', startDate, 'to', endDate, 'timeframe:', timeframe);
          await indicator.enable('ES', startDate, endDate, timeframe);
        } else {
          // Already enabled - check if we need to update (date/timeframe changed)
          const needsUpdate = indicator.startDate !== startDate ||
                             indicator.endDate !== endDate ||
                             indicator.timeframe !== timeframe;
          if (needsUpdate) {
            console.log('[ESChart] Updating VWAP for date range:', startDate, 'to', endDate);
            await indicator.update('ES', startDate, endDate, timeframe);
          }
        }
      } else {
        // Disable if currently enabled
        if (isEnabled) {
          indicator.disable();
          console.log('[ESChart] VWAP indicator disabled');
        }
      }
    };

    handleVwap();
  }, [chartReady, showVWAP, timeframe, vwapDateRange]);

  // Renko only: keep the VWAP brick-slot alignment fresh as completed bricks
  // stream in (they update the `candles` prop), then re-align the drawn series.
  // The enable/update effect above intentionally ignores `candles`, so this is
  // what extends/realigns VWAP live without refetching.
  useEffect(() => {
    const indicator = vwapIndicatorRef.current;
    if (!indicator || !isRenko || !showVWAP || !indicator.isEnabled()) return;
    indicator.setRenkoSlots((candles || []).map(c => c.time));
    indicator.updateSeriesData();
  }, [candles, isRenko, showVWAP]);

  // Toggle VWAP ±1σ bands
  useEffect(() => {
    if (vwapIndicatorRef.current) vwapIndicatorRef.current.setBand1(showVWAPBand1);
  }, [showVWAPBand1]);

  // Toggle VWAP ±2σ bands
  useEffect(() => {
    if (vwapIndicatorRef.current) vwapIndicatorRef.current.setBand2(showVWAPBand2);
  }, [showVWAPBand2]);

  // VWAP session mode (full / full + RTH overlay)
  useEffect(() => {
    if (vwapIndicatorRef.current) {
      vwapIndicatorRef.current.setSessionMode(vwapSessionMode);
      console.log('[ESChart] VWAP session mode:', vwapSessionMode);
    }
  }, [vwapSessionMode]);

  // Handle Volume Profile indicator visibility toggle
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current || !chartContainerRef.current) return;

    // Create Volume Profile plugin instance if not exists
    if (!volumeProfilePluginRef.current) {
      volumeProfilePluginRef.current = new VolumeProfilePlugin(
        chartRef.current,
        seriesRef.current,
        chartContainerRef.current
      );
      console.log('[ESChart] Volume Profile plugin created');
    }

    const handleVolumeProfileToggle = async () => {
      if (showVolumeProfile) {
        if (!volumeProfilePluginRef.current.isEnabled()) {
          const today = date || new Date().toISOString().split('T')[0];
          console.log('[ESChart] Enabling Volume Profile for date:', today);
          await volumeProfilePluginRef.current.enable('ES', today, today, timeframe);
        }
      } else {
        if (volumeProfilePluginRef.current.isEnabled()) {
          volumeProfilePluginRef.current.disable();
          console.log('[ESChart] Volume Profile disabled');
        }
      }
    };

    handleVolumeProfileToggle();

    return () => {
      if (volumeProfilePluginRef.current) {
        volumeProfilePluginRef.current.disable();
        volumeProfilePluginRef.current = null;
      }
    };
  }, [chartReady, showVolumeProfile, date, timeframe]);

  // Note: VWAP updates are handled by the main VWAP toggle effect (line 639)
  // which already has date, timeframe, and candles in its dependency array.
  // A separate update effect was causing double rendering and overwriting
  // multi-day VWAP data with single-day data.

  // Update Volume Profile when date or timeframe changes (if enabled)
  // Real-time updates are handled internally by the plugin via _startRealtimeUpdates() polling
  useEffect(() => {
    if (volumeProfilePluginRef.current && volumeProfilePluginRef.current.isEnabled()) {
      const today = date || new Date().toISOString().split('T')[0];
      volumeProfilePluginRef.current.update('ES', today, today, timeframe);
      console.log('[ESChart] Volume Profile updated for date:', today);
    }
  }, [date, timeframe]);

  // Handle Delta Profile toggle (controls showDeltaProfile on VolumeProfilePlugin)
  useEffect(() => {
    if (volumeProfilePluginRef.current) {
      volumeProfilePluginRef.current.setShowDeltaProfile(showDeltaProfile);
      console.log('[ESChart] Delta Profile toggled:', showDeltaProfile);
    }
  }, [showDeltaProfile]);

  // Handle VP session mode toggle (Full vs Split)
  useEffect(() => {
    if (volumeProfilePluginRef.current && volumeProfilePluginRef.current.isEnabled()) {
      volumeProfilePluginRef.current.setSessionMode(vpSessionMode);
      console.log('[ESChart] VP session mode:', vpSessionMode);
    }
  }, [vpSessionMode]);

  // ── Naked POC plugin lifecycle: two parent-gated layers in one plugin.
  // vp layer = Vol Profile sub-toggle, follows the VP session mode;
  // tpo layer = TPO Profile sub-toggle, follows the TPO session scope.
  // Creation/destruction is chart-scoped; layer flags/modes reconfigure in
  // place (no detach/refetch churn on toggles). ──
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;

    if (!nakedPOCPluginRef.current) {
      nakedPOCPluginRef.current = new NakedPOCPlugin(chartRef.current, seriesRef.current);
    }

    return () => {
      if (nakedPOCPluginRef.current) {
        nakedPOCPluginRef.current.disable();
        nakedPOCPluginRef.current = null;
      }
    };
  }, [chartReady]);

  useEffect(() => {
    if (!nakedPOCPluginRef.current) return;
    nakedPOCPluginRef.current.configure({
      vp: { enabled: !!(showVolumeProfile && showNakedPOC), mode: vpSessionMode },
      tpo: { enabled: !!(showTPOProfile && showNakedTpoPOC), mode: tpoSessionScope },
    });
  }, [chartReady, showVolumeProfile, showNakedPOC, showTPOProfile, showNakedTpoPOC, vpSessionMode, tpoSessionScope]);

  // ── HVN zone bands: parent-gated under Vol Profile; settle-static data so
  // there is no mode or date dependence. Creation is chart-scoped; the toggle
  // enables/disables in place. ──
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;
    if (!hvnZonesPluginRef.current) {
      hvnZonesPluginRef.current = new HVNZonesPlugin(chartRef.current, seriesRef.current);
    }
    return () => {
      if (hvnZonesPluginRef.current) {
        hvnZonesPluginRef.current.disable();
        hvnZonesPluginRef.current = null;
      }
    };
  }, [chartReady]);

  useEffect(() => {
    if (!hvnZonesPluginRef.current) return;
    if (showVolumeProfile && showHVNZones) {
      if (!hvnZonesPluginRef.current.isEnabled()) hvnZonesPluginRef.current.enable();
    } else if (hvnZonesPluginRef.current.isEnabled()) {
      hvnZonesPluginRef.current.disable();
    }
  }, [chartReady, showVolumeProfile, showHVNZones]);

  // ── TPO Plugin lifecycle ──
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current || !chartContainerRef.current) return;

    if (!tpoPluginRef.current) {
      tpoPluginRef.current = new TPOPlugin(
        chartRef.current,
        seriesRef.current,
        chartContainerRef.current
      );
      console.log('[ESChart] TPO plugin created');
    }

    const toggle = async () => {
      if (showTPOProfile) {
        if (!tpoPluginRef.current.isEnabled()) {
          const today = date || new Date().toISOString().split('T')[0];
          console.log('[ESChart] Enabling TPO for date:', today);
          tpoPluginRef.current.renderMode = tpoRenderMode;
          tpoPluginRef.current.sessionScope = tpoSessionScope;
          tpoPluginRef.current.displayStyle = tpoDisplayStyle;
          await tpoPluginRef.current.enable('ES', today, today, timeframe);
        }
      } else {
        if (tpoPluginRef.current.isEnabled()) {
          tpoPluginRef.current.disable();
          console.log('[ESChart] TPO disabled');
        }
      }
    };
    toggle();

    return () => {
      if (tpoPluginRef.current) {
        tpoPluginRef.current.disable();
        tpoPluginRef.current = null;
      }
    };
  }, [chartReady, showTPOProfile, date, timeframe]);

  // ── Candle Profile (per-30m-candle VP + TPO) lifecycle - planning chart ──
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current || !chartContainerRef.current) return;

    if (!candleProfilePluginRef.current) {
      candleProfilePluginRef.current = new CandleProfilePlugin(
        chartRef.current,
        seriesRef.current,
        chartContainerRef.current
      );
    }

    const plugin = candleProfilePluginRef.current;
    const toggle = async () => {
      if (showCandleProfiles) {
        plugin.timeframe = '30m';
        plugin.sessionType = (candleProfileSessionType || 'eth').toLowerCase();
        plugin.store.showVolume = showCandleProfileVolume;
        plugin.store.showTpo = showCandleProfileTpo;
        plugin.store.widthFraction = candleProfileWidthFraction;
        if (!plugin.isEnabled()) {
          await plugin.enable();
        }
      } else if (plugin.isEnabled()) {
        plugin.disable();
      }
    };
    toggle();

    return () => {
      if (candleProfilePluginRef.current) {
        candleProfilePluginRef.current.disable();
        candleProfilePluginRef.current = null;
      }
    };
  }, [chartReady, showCandleProfiles]);

  // React to per-candle profile option changes without re-creating the plugin
  useEffect(() => {
    const plugin = candleProfilePluginRef.current;
    if (!plugin || !plugin.isEnabled()) return;
    plugin.setShowVolume(showCandleProfileVolume);
    plugin.setShowTpo(showCandleProfileTpo);
    plugin.setSessionType(candleProfileSessionType);
    plugin.setWidthFraction(candleProfileWidthFraction);
  }, [showCandleProfileVolume, showCandleProfileTpo, candleProfileSessionType, candleProfileWidthFraction]);

  // ── Thin candle bodies (planning chart) ──
  // Decouple candle body width from barSpacing: native body is transparent
  // (see color effect) and we paint fixed-width bodies here.
  // Lifecycle is keyed only on chartReady/thinCandles so it does NOT churn on
  // the planning page's frequent re-renders (live SSE + sliders). Width/color
  // changes flow through the separate options effect below (no re-attach).
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;

    if (thinCandles) {
      if (!thinCandlesPluginRef.current) {
        thinCandlesPluginRef.current = new ThinCandlesPlugin(chartRef.current, seriesRef.current);
      }
      thinCandlesPluginRef.current.setOptions({
        widthPx: candleBodyWidthPx,
        upColor: effectiveCandleColors.upColor,
        downColor: effectiveCandleColors.downColor,
      });
      if (!thinCandlesPluginRef.current.isEnabled()) {
        thinCandlesPluginRef.current.enable();
      }
    } else if (thinCandlesPluginRef.current) {
      thinCandlesPluginRef.current.disable();
      thinCandlesPluginRef.current = null;
    }

    return () => {
      if (thinCandlesPluginRef.current) {
        thinCandlesPluginRef.current.disable();
        thinCandlesPluginRef.current = null;
      }
    };
  }, [chartReady, thinCandles]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Update thin-candle width/colors without re-attaching (primitive-string deps
  // avoid the object-identity churn of candleColors).
  useEffect(() => {
    if (thinCandlesPluginRef.current) {
      thinCandlesPluginRef.current.setOptions({
        widthPx: candleBodyWidthPx,
        upColor: effectiveCandleColors.upColor,
        downColor: effectiveCandleColors.downColor,
      });
    }
  }, [candleBodyWidthPx, effectiveCandleColors.upColor, effectiveCandleColors.downColor]);

  // Renko rotation overlay: zig-zag legs (reach-styled) + directional-bias ribbon.
  // Render-only; the page supplies legs/segments already mapped to the chart's
  // synthetic brick times. Keyed only on chartReady/showRotations so it does not
  // re-attach on data churn.
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;
    if (showRotations) {
      if (!rotationsPluginRef.current) {
        rotationsPluginRef.current = new RotationsPlugin(chartRef.current, seriesRef.current);
      }
      if (!rotationsPluginRef.current.isEnabled()) rotationsPluginRef.current.enable();
      if (rotationData) rotationsPluginRef.current.setData(rotationData);
    } else if (rotationsPluginRef.current) {
      rotationsPluginRef.current.disable();
      rotationsPluginRef.current = null;
    }
    return () => {
      if (rotationsPluginRef.current) {
        rotationsPluginRef.current.disable();
        rotationsPluginRef.current = null;
      }
    };
  }, [chartReady, showRotations]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Push new rotation data without re-attaching.
  useEffect(() => {
    if (rotationsPluginRef.current && rotationData) {
      rotationsPluginRef.current.setData(rotationData);
    }
  }, [rotationData]);

  // Apply bar-spacing (separation) overrides live
  useEffect(() => {
    if (!chartReady || !chartRef.current || barSpacing == null) return;
    try {
      chartRef.current.timeScale().applyOptions({ barSpacing });
    } catch (e) { /* ignore */ }
  }, [chartReady, barSpacing]);

  // Handle TPO date/timeframe update
  useEffect(() => {
    if (tpoPluginRef.current && tpoPluginRef.current.isEnabled()) {
      const today = date || new Date().toISOString().split('T')[0];
      tpoPluginRef.current.update('ES', today, today, timeframe);
    }
  }, [date, timeframe]);

  // Handle TPO render mode toggle
  useEffect(() => {
    if (tpoPluginRef.current) {
      tpoPluginRef.current.setRenderMode(tpoRenderMode);
      console.log('[ESChart] TPO render mode:', tpoRenderMode);
    }
  }, [tpoRenderMode]);

  // Handle TPO session scope toggle
  useEffect(() => {
    if (tpoPluginRef.current && tpoPluginRef.current.isEnabled()) {
      tpoPluginRef.current.setSessionScope(tpoSessionScope);
      console.log('[ESChart] TPO session scope:', tpoSessionScope);
    }
  }, [tpoSessionScope]);

  // Handle TPO display style toggle
  useEffect(() => {
    if (tpoPluginRef.current && tpoPluginRef.current.isEnabled()) {
      tpoPluginRef.current.setDisplayStyle(tpoDisplayStyle);
      console.log('[ESChart] TPO display style:', tpoDisplayStyle);
    }
  }, [tpoDisplayStyle]);

  // Handle CVD indicator visibility toggle
  useEffect(() => {
    if (!chartReady || !chartRef.current || !seriesRef.current) return;

    // Create CVD indicator instance if not exists
    if (!cvdIndicatorRef.current) {
      cvdIndicatorRef.current = new CVDIndicator(chartRef.current, seriesRef.current);
      console.log('[ESChart] CVD indicator created');
    }

    if (showCVD) {
      cvdIndicatorRef.current.enable(candlesRef.current);
    } else {
      cvdIndicatorRef.current.disable();
    }

    return () => {
      if (cvdIndicatorRef.current) {
        cvdIndicatorRef.current.disable();
        cvdIndicatorRef.current = null;
      }
    };
  }, [chartReady, showCVD]);

  // Update CVD data when candles change (without recreating the indicator)
  useEffect(() => {
    if (cvdIndicatorRef.current && showCVD) {
      cvdIndicatorRef.current.update(candlesRef.current);
    }
  }, [candles, showCVD]);

  // Margin coordination callback for CVD sub-pane (exposure is now in its own chart)
  const updateSubChartMargins = useCallback(() => {
    if (!chartRef.current) return;
    const cvdOn = cvdIndicatorRef.current?.isEnabled();

    if (cvdOn) {
      chartRef.current.priceScale('right').applyOptions({ scaleMargins: { top: 0.05, bottom: 0.25 } });
      chartRef.current.priceScale('left').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    } else {
      chartRef.current.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
    }
  }, []);

  // Set margin callback on CVD indicator when it's created
  useEffect(() => {
    if (cvdIndicatorRef.current) {
      cvdIndicatorRef.current.setMarginCallback(updateSubChartMargins);
    }
  }, [chartReady, showCVD, updateSubChartMargins]);

  // Manage left price scale visibility - shared by CVD and IV
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    const leftNeeded = showCVD || showIV;
    try {
      chartRef.current.priceScale('left').applyOptions({ visible: leftNeeded });
    } catch (e) { /* chart may be tearing down */ }
  }, [chartReady, showCVD, showIV]);

  // Handle Exposure pane: add/remove a pane on the main chart (shares time axis)
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    if (!showExposure) return;

    const chart = chartRef.current;

    // Add a new pane to the main chart for exposure data
    // preserveEmptyPane: without it the library auto-removes the pane the
    // moment its last series is removed (remove-then-rebuild swaps, empty
    // emissions), and later addSeries on the detached pane throws and can
    // unmount the whole chart (premium-units crash, 2026-08-11).
    const pane = chart.addPane(true);
    pane.setStretchFactor(0.35);  // ~26% of chart height
    exposurePaneRef.current = pane;

    // Create indicator, enable, then fetch data from dedicated endpoint
    exposureIndicatorRef.current = new ExposureIndicator(chart, pane);
    exposureIndicatorRef.current.enable();
    exposureIndicatorRef.current.setMode(exposureMode);
    const today = new Date().toISOString().split('T')[0];
    authFetch(`${CONFIG.apiUrl}/api/greeks/exposure?start_date=${date}&timeframe=${timeframe}&end_date=${today}&expiry_mode=${expiryMode}&underlying=${underlying}${exposureLiveSmile ? '&smile=live' : ''}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.candles && exposureIndicatorRef.current?.isEnabled()) {
          exposureCandlesRef.current = data.candles;
          exposureIndicatorRef.current.setMode(exposureMode);
          exposureIndicatorRef.current.update(data.candles);
        }
      })
      .catch(e => console.warn('[ESChart] Exposure fetch error:', e));

    // Live updates: upsert the forming exposure row's full-book net sums
    // into the fetched series. The row arrives on the dedicated greeks 1m
    // channel at most once / 5s (producer-throttled) carrying net_*/net_u_*
    // verbatim; bucket it LAST-per-window exactly like the REST endpoint
    // (last_exposure_rows_by_window). All expiry modes publish on the same
    // channel, so the mode filter is required.
    let unsubExposureRow = null;
    const periodSec = EXPOSURE_TF_SECONDS[timeframe];
    if (periodSec) {
      const wantMode = expiryMode || '0dte';
      const netFields = ['net_gex', 'net_dex', 'net_vex', 'net_cex',
                         'net_u_gex', 'net_u_dex', 'net_u_vex', 'net_u_cex'];
      unsubExposureRow = unifiedSSE.subscribe(`greeks:${underlying}:1m`,
          exposureLiveSmile ? 'greeks_exposure_row_live' : 'greeks_exposure_row', (message) => {
        try {
          if (message.mode && message.mode !== wantMode) return;
          if (message.underlying && message.underlying !== underlying) return;
          const row = message.data;
          if (!row || row.time == null) return;
          if (!exposureIndicatorRef.current?.isEnabled()) return;

          const bucket = Math.floor(row.time / periodSec) * periodSec;
          const point = { time: bucket };
          for (const f of netFields) point[f] = Number(row[f] || 0);

          const candles = exposureCandlesRef.current;
          const idx = candles.findIndex(c => c.time === bucket);
          if (idx >= 0) {
            candles[idx] = point;
          } else {
            candles.push(point);
            candles.sort((a, b) => a.time - b.time);
          }
          exposureIndicatorRef.current.update(candles);
        } catch (e) {
          console.warn('[ESChart] Exposure live row error:', e);
        }
      });
    }

    console.log('[ESChart] Exposure pane created');

    return () => {
      if (unsubExposureRow) unsubExposureRow();
      if (exposureIndicatorRef.current) {
        exposureIndicatorRef.current.disable();
        exposureIndicatorRef.current = null;
      }
      exposureCandlesRef.current = [];
      if (exposurePaneRef.current) {
        try {
          chart.removePane(exposurePaneRef.current.paneIndex());
        } catch (e) {
          // Pane may already be removed during chart teardown
        }
        exposurePaneRef.current = null;
      }
    };
  }, [chartReady, showExposure, date, timeframe, expiryMode, underlying, exposureLiveSmile]);

  // Handle the Shape History pane (IV Skew sub-toggle): add/remove a
  // sub-pane fed by the GEX plugin's derived per-minute smile geometry
  // (onIvSkewShape callback: the plugin re-emits on every REST fetch and
  // SSE minute, so the pane stays current without its own fetch or SSE
  // subscription).
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    if (!showIvSkewShape || isVolumeChart) return;

    const chart = chartRef.current;
    // preserveEmptyPane: without it the library auto-removes the pane the
    // moment its last series is removed (remove-then-rebuild swaps, empty
    // emissions), and later addSeries on the detached pane throws and can
    // unmount the whole chart (premium-units crash, 2026-08-11).
    const pane = chart.addPane(true);
    pane.setStretchFactor(0.25);
    ivSkewShapePaneRef.current = pane;

    ivSkewShapeIndicatorRef.current = new IvSkewShapeIndicator(chart, pane);
    ivSkewShapeIndicatorRef.current.enable(ivSkewShapeSeriesRef.current);

    console.log('[ESChart] Shape History pane created');

    return () => {
      if (ivSkewShapeIndicatorRef.current) {
        ivSkewShapeIndicatorRef.current.disable();
        ivSkewShapeIndicatorRef.current = null;
      }
      if (ivSkewShapePaneRef.current) {
        try {
          chart.removePane(ivSkewShapePaneRef.current.paneIndex());
        } catch (e) {
          // Pane may already be removed during chart teardown
        }
        ivSkewShapePaneRef.current = null;
      }
    };
  }, [chartReady, showIvSkewShape, isVolumeChart]);

  // Handle the Flow pane (Options Volume sub-toggle): add/remove a
  // sub-pane fed by the GEX plugin's derived net dealer flow series
  // (onVolFlow callback: the plugin re-emits on every REST fetch and
  // SSE upsert, so the pane stays current without its own fetch or SSE
  // subscription).
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    if (!showVolFlowPane || isVolumeChart) return;

    const chart = chartRef.current;
    // preserveEmptyPane: without it the library auto-removes the pane the
    // moment its last series is removed (remove-then-rebuild swaps, empty
    // emissions), and later addSeries on the detached pane throws and can
    // unmount the whole chart (premium-units crash, 2026-08-11).
    const pane = chart.addPane(true);
    pane.setStretchFactor(0.25);
    volFlowPaneRef.current = pane;

    volFlowIndicatorRef.current = new VolFlowIndicator(chart, pane);
    volFlowIndicatorRef.current.enable(volFlowSeriesRef.current);

    console.log('[ESChart] Flow pane created');

    return () => {
      if (volFlowIndicatorRef.current) {
        volFlowIndicatorRef.current.disable();
        volFlowIndicatorRef.current = null;
      }
      if (volFlowPaneRef.current) {
        try {
          chart.removePane(volFlowPaneRef.current.paneIndex());
        } catch (e) {
          // Pane may already be removed during chart teardown
        }
        volFlowPaneRef.current = null;
      }
    };
  }, [chartReady, showVolFlowPane, isVolumeChart]);

  // Flip the Exposure weighting (dealer / absolute) without a refetch: the
  // indicator re-renders from its cached candles.
  useEffect(() => {
    if (exposureIndicatorRef.current?.isEnabled()) {
      exposureIndicatorRef.current.setMode(exposureMode);
    }
  }, [exposureMode]);

  // Update level data in plugin when it changes
  useEffect(() => {
    if (greeksPluginRef.current && levelData) {
      greeksPluginRef.current.setLevelData(levelData);
    }
  }, [levelData]);

  // Per-day ES/SPX + ES/SPY session ratios for the tooltip strike line.
  // Fetched regardless of indicator toggles so the strikes always resolve.
  // Refetched every 5 minutes to pick up newly written T-1 ratio rows and
  // the 18:00 ET session roll (getLatestSessionDate advances the range end).
  useEffect(() => {
    if (!chartReady || !date) return;
    let cancelled = false;
    const fetchRatios = () => {
      authFetch(`${CONFIG.apiUrl}/api/greeks/session-ratios?start_date=${date}&end_date=${getLatestSessionDate()}`)
        .then(resp => (resp.ok ? resp.json() : null))
        .then(data => {
          if (cancelled || !data) return;
          if (data.ratios) sessionRatiosRef.current = data.ratios;
          setRatiosVersion(v => v + 1);
        })
        .catch(() => {});  // swallow fetch errors, keep the poll alive
    };
    fetchRatios();
    const interval = setInterval(fetchRatios, 300000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [chartReady, date]);

  // Redraw GEX heatmap when candle data changes (ensures heatmap aligns with new time range)
  useEffect(() => {
    if (greeksPluginRef.current && greeksPluginRef.current.isEnabled() && candles && candles.length > 0) {
      // Use requestAnimationFrame to ensure chart has processed the new candle data
      requestAnimationFrame(() => {
        if (greeksPluginRef.current && greeksPluginRef.current.isEnabled()) {
          console.log('[ESChart] Redrawing GEX after candle data update:', candles.length, 'candles');
          greeksPluginRef.current.draw();
        }
      });
    }
  }, [candles]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'row' }}>
      <div
        ref={chartContainerRef}
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          position: 'relative',
        }}
      />

      {/* Underlying Compare strip: flex sibling of the chart host; inserting/removing
          it resizes the host, which the ResizeObserver above refits automatically. */}
      {showUnderlyingCompare && !isVolumeChart && chartReady && seriesRef.current && (
        <UnderlyingCompare
          series={seriesRef.current}
          chart={chartRef.current}
          mode={underlyingCompareMode}
          greek={underlyingCompareGreek}
          expiryMode={expiryMode}
          timeframe={timeframe}
          date={date}
          endDate={endDate || date}
          timezone={timezone}
          sessionRatiosRef={sessionRatiosRef}
          zeroLevels={underlyingCompareZeroLevels}
          gammaLevels={underlyingCompareGammaLevels}
        />
      )}

      {/* Volume stats overlay (only for volume-based charts) */}
      {isVolumeChart && volumeStats && (
        <div style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '8px 12px',
          background: 'rgba(26, 26, 26, 0.9)',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          fontSize: 11,
          color: '#d1d5db',
          fontFamily: 'monospace',
        }}>
          <div style={{ color: '#8b92b2', marginBottom: 4 }}>
            Volume Candles ({volumeSize.toLocaleString()})
          </div>
          <div>Candles: {volumeStats.count}</div>
          <div>Volume: {volumeStats.totalVolume?.toLocaleString()}</div>
          <div style={{ color: volumeStats.finalCvd >= 0 ? '#22c55e' : '#ef4444' }}>
            CVD: {volumeStats.finalCvd?.toLocaleString()}
          </div>
        </div>
      )}

      {/* Loading overlay (for volume candles) */}
      {isVolumeChart && volumeLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '12px 24px',
          background: 'rgba(26, 26, 26, 0.95)',
          border: '1px solid #2a2a2a',
          borderRadius: 4,
          color: '#d1d5db',
        }}>
          Loading volume candles...
        </div>
      )}
    </div>
  );
}

export default ESChart;
